import React, { useEffect, useMemo, useRef, useState } from 'react';
import { db, functions, storage } from '../firebase';
import { collection, doc, getDocs, onSnapshot, query, setDoc, deleteDoc, updateDoc, where, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { buildGameRecap } from '../utils/gameRecap';
import { DEFAULT_RECAP_STYLE, RECAP_STYLE_OPTIONS } from '../utils/recapStyles';
import { TEAM_STATS_TEAMS, getStatsCollectionRoot } from '../utils/teamStatsConfig';

/** Some browsers send an empty `File.type` for camera rolls; Storage rules expect image/* or null. */
function recapPhotoUploadMetadata(file) {
  const raw = typeof file?.type === 'string' ? file.type.trim() : '';
  if (raw.startsWith('image/')) return { contentType: raw };
  const ext = file?.name?.split('.').pop()?.toLowerCase() || 'jpeg';
  const byExt = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    gif: 'image/gif',
  };
  return { contentType: byExt[ext] || 'image/jpeg' };
}

export default function HockeyStatTrackerInner({ teamId, initialAuthToken = null }) {
  // --- state ---
  const [loading, setLoading] = useState(false);
  const [isStatsVisible, setIsStatsVisible] = useState(true);
  const [selectedGameId, setSelectedGameId] = useState(null);
  const [games, setGames] = useState([]);
  const [lineup, setLineup] = useState([]);
  const [teamRoster, setTeamRoster] = useState([]); // Master team roster
  const [inputName, setInputName] = useState('');
  const [inputNumber, setInputNumber] = useState('');
  const [inputPosition, setInputPosition] = useState('');
  const [gameData, setGameData] = useState({
    score: { us: 0, them: 0 },
    shots: { us: 0, them: 0 },
    shotsByPeriod: { us: {}, them: {} },  // Track shots per period
    goalieSaves: 0,
    gameLog: [],
    penalties: [],
    currentPeriod: 1,
  });
  const [newGameDetails, setNewGameDetails] = useState({
    opponent: '',
    date: new Date().toISOString().split('T')[0],
    location: '',
  });
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [rosterMessage, setRosterMessage] = useState('');
  const [isRosterVisible, setIsRosterVisible] = useState(true);
  const [penaltyPlayer, setPenaltyPlayer] = useState('');
  const [penaltyType, setPenaltyType] = useState('');
  const [penaltyDuration, setPenaltyDuration] = useState('1:30');
  const [useCustomPenalty, setUseCustomPenalty] = useState(false);
  const [customPenaltyMins, setCustomPenaltyMins] = useState('');
  const [customPenaltySecs, setCustomPenaltySecs] = useState('');
  const [editingPlayerName, setEditingPlayerName] = useState(null);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [newPlayerNumber, setNewPlayerNumber] = useState('');
  const [newPlayerPosition, setNewPlayerPosition] = useState('');
  const [scorerName, setScorerName] = useState('');
  const [assist1Name, setAssist1Name] = useState('');
  const [assist2Name, setAssist2Name] = useState('');
  const [assist3Name, setAssist3Name] = useState('');
  const [showArchivedGames, setShowArchivedGames] = useState(false);
  const [rosterSortBy, setRosterSortBy] = useState('default');
  const [editingLogIndex, setEditingLogIndex] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [selectedSkaterName, setSelectedSkaterName] = useState('');
  const [isBackfillingHistory, setIsBackfillingHistory] = useState(false);
  /** Which goalie is "in net" right now — stats (saves, goals allowed) are attributed to this goalie. Only one active at a time. */
  const [activeGoalieName, setActiveGoalieName] = useState(null);
  /** After ending/re-archiving a game: show "Generate recap" / "Done" modal. Data kept until user dismisses. */
  const [endGameModal, setEndGameModal] = useState(null);
  /** True while AI recap is being generated (Cloud Function call in progress). */
  const [isGeneratingRecap, setIsGeneratingRecap] = useState(false);
  /** Optional 1 photo for recap (file object). */
  const [recapPhotoFile, setRecapPhotoFile] = useState(null);
  /** Gladiator of the Game – player name from lineup. */
  const [gladiatorOfGameName, setGladiatorOfGameName] = useState('');
  /** Why they were Gladiator of the Game. */
  const [gladiatorOfGameReason, setGladiatorOfGameReason] = useState('');
  /** Optional photo of the Gladiator of the Game (smaller, shown in newspaper callout). */
  const [gladiatorPhotoFile, setGladiatorPhotoFile] = useState(null);
  /** Gazette / AI recap tone (must match Cloud Function allowlist). */
  const [recapStyle, setRecapStyle] = useState(DEFAULT_RECAP_STYLE);
  /** Family quotes from Gladiators TV for this game (End Game modal). */
  const [fanComments, setFanComments] = useState([]);
  /** Selected fan comment id (or '' to skip). */
  const [selectedFanCommentId, setSelectedFanCommentId] = useState('');
  /** Status line while generating Gazette (photos → AI → save). */
  const [recapProgressStatus, setRecapProgressStatus] = useState('');
  
  // Section visibility states (persisted to localStorage)
  const [sectionVisibility, setSectionVisibility] = useState({
    scoreboard: true,
    goalie: true,
    logGoal: true,
    periodBreakdown: true,
    roster: true,
    plusMinus: true,
    penalties: true,
    stats: true,
    gameLog: true
  });

  const validTeamId = typeof teamId === 'string' && teamId.trim().length > 0;

  /* ===========================================================
     LOCAL CACHE (instant offline restore on reload)
     =========================================================== */
  const LOCAL_KEY = `gcg-statcache:${teamId}`;

  // Game Management panel visibility (persisted)
  const [isGameMgmtOpen, setIsGameMgmtOpen] = useState(true);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`${LOCAL_KEY}:gmOpen`);
      if (raw === '0' || raw === '1') {
        setIsGameMgmtOpen(raw === '1');
      } else if (selectedGameId) {
        setIsGameMgmtOpen(false);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [LOCAL_KEY, selectedGameId]);
  useEffect(() => {
    try { localStorage.setItem(`${LOCAL_KEY}:gmOpen`, isGameMgmtOpen ? '1' : '0'); } catch {}
  }, [LOCAL_KEY, isGameMgmtOpen]);

  // Master Team Roster panel (inside Game Management) — persisted
  const [isMasterRosterOpen, setIsMasterRosterOpen] = useState(true);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`${LOCAL_KEY}:masterRosterOpen`);
      if (raw === '0' || raw === '1') setIsMasterRosterOpen(raw === '1');
    } catch {}
  }, [LOCAL_KEY]);
  useEffect(() => {
    try {
      localStorage.setItem(`${LOCAL_KEY}:masterRosterOpen`, isMasterRosterOpen ? '1' : '0');
    } catch {}
  }, [LOCAL_KEY, isMasterRosterOpen]);

  // Ref: skip next debounced save when update came from Firestore or from cache (multi-tab + hydrate safety)
  const skipNextSaveFromRemoteRef = useRef(false);
  const saveTimer = useRef(null);
  /** Keeps games-list snapshot from re-subscribing when selection changes (stable onSnapshot). */
  const selectedGameIdRef = useRef(selectedGameId);
  selectedGameIdRef.current = selectedGameId;

  // Hydrate on first render
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOCAL_KEY);
      if (raw) {
        const { lineup: lsLineup, gameData: lsGameData, selectedGameId: lsSelected } = JSON.parse(raw);
        if (Array.isArray(lsLineup) && lsLineup.length > 0) setLineup(lsLineup);
        if (lsGameData && typeof lsGameData === 'object') {
          // Ensure shotsByPeriod exists for backwards compatibility
          if (!lsGameData.shotsByPeriod) {
            lsGameData.shotsByPeriod = { us: {}, them: {} };
          }
          setGameData(lsGameData);
        }
        if (lsSelected) setSelectedGameId(lsSelected);
        // Don't overwrite Firestore with possibly stale cache on first run
        skipNextSaveFromRemoteRef.current = true;
      }
      
      // Load section visibility
      const visRaw = localStorage.getItem(`${LOCAL_KEY}:sectionVis`);
      if (visRaw) {
        setSectionVisibility(JSON.parse(visRaw));
      }
    } catch (e) { console.warn('Local cache read failed:', e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [LOCAL_KEY]);

  // Save whenever key states change
  useEffect(() => {
    try {
      const payload = JSON.stringify({ lineup, gameData, selectedGameId });
      localStorage.setItem(LOCAL_KEY, payload);
    } catch (e) { console.warn('Local cache write failed:', e); }
  }, [LOCAL_KEY, lineup, gameData, selectedGameId]);
  
  // Save section visibility
  useEffect(() => {
    try {
      localStorage.setItem(`${LOCAL_KEY}:sectionVis`, JSON.stringify(sectionVisibility));
    } catch (e) { console.warn('Section visibility save failed:', e); }
  }, [LOCAL_KEY, sectionVisibility]);
  
  // Helper to toggle section visibility
  const toggleSection = (section) => {
    setSectionVisibility(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  // Helper to show/hide all sections
  const toggleAllSections = () => {
    const allVisible = Object.values(sectionVisibility).every(v => v);
    const newState = {
      scoreboard: !allVisible,
      goalie: !allVisible,
      logGoal: !allVisible,
      periodBreakdown: !allVisible,
      roster: !allVisible,
      plusMinus: !allVisible,
      penalties: !allVisible,
      stats: !allVisible,
      gameLog: !allVisible
    };
    setSectionVisibility(newState);
  };

  // Auto-collapse heavy panels on mobile first visit
  useEffect(() => {
    if (!localStorage.getItem(`${LOCAL_KEY}:gmOpen`)) {
      if (window.matchMedia && window.matchMedia('(max-width: 576px)').matches) {
        setIsGameMgmtOpen(false);
      }
    }
    if (window.matchMedia && window.matchMedia('(max-width: 576px)').matches) {
      setIsRosterVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // --- online/offline ---
  useEffect(() => {
    const handler = () => setIsOffline(!navigator.onLine);
    window.addEventListener('online', handler);
    window.addEventListener('offline', handler);
    return () => {
      window.removeEventListener('online', handler);
      window.removeEventListener('offline', handler);
    };
  }, []);

  // --- firestore refs (segment form matches AdminPortal / rules path `{stats|statsDev}/{teamId}/...`) ---
  const statsRoot = getStatsCollectionRoot();
  const gamesColRef = useMemo(() => {
    if (!validTeamId) return null;
    return collection(db, statsRoot, teamId, 'games');
  }, [teamId, validTeamId, statsRoot]);

  const teamRosterRef = useMemo(() => {
    if (!validTeamId) return null;
    return doc(db, statsRoot, teamId, 'config', 'teamRoster');
  }, [teamId, validTeamId, statsRoot]);

  const gameHistoryColRef = useMemo(() => {
    if (!validTeamId) return null;
    return collection(db, statsRoot, teamId, 'gameHistory');
  }, [teamId, validTeamId, statsRoot]);

  /** Build snapshot for master record (gameHistory). Per-goalie stats are in lineup; goalieSaves is total. Optional recap included when provided. */
  const buildGameHistorySnapshot = (gameId, details, lineupData, gameDataSnapshot, endedAtIso, recap = null) => {
    const gd = gameDataSnapshot || {};
    const lineupArr = Array.isArray(lineupData) ? lineupData : [];
    const totalGoalieSaves = lineupArr
      .filter((p) => p.isGoalie)
      .reduce((s, p) => s + (p.saves || 0), 0);
    const goalieSavesTotal = totalGoalieSaves > 0 ? totalGoalieSaves : (gd.goalieSaves ?? 0);
    const snapshot = {
      gameId,
      endedAt: endedAtIso || new Date().toISOString(),
      details: details || {},
      lineup: lineupArr,
      showOnHome: true,
      gameData: {
        score: gd.score || { us: 0, them: 0 },
        shots: gd.shots || { us: 0, them: 0 },
        shotsByPeriod: gd.shotsByPeriod || { us: {}, them: {} },
        goalieSaves: goalieSavesTotal,
        gameLog: Array.isArray(gd.gameLog) ? gd.gameLog : [],
        penalties: Array.isArray(gd.penalties) ? gd.penalties : [],
        currentPeriod: gd.currentPeriod ?? 1,
      },
    };
    if (recap && typeof recap === 'object' && recap.headline != null) {
      snapshot.recap = recap;
    }
    return snapshot;
  };

  // --- load games list ---
  useEffect(() => {
    if (!gamesColRef) return;
    const q = query(gamesColRef);
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setGames(list);
        const sel = selectedGameIdRef.current;
        if (!sel && list.length > 0) {
          // Only select from non-archived games
          const activeGames = list.filter(g => !g.archived);
          if (activeGames.length > 0) {
            const mostRecent = activeGames.reduce((p, c) => {
              const dp = p?.details?.date ? new Date(p.details.date) : new Date(0);
              const dc = c?.details?.date ? new Date(c.details.date) : new Date(0);
              return dc > dp ? c : p;
            });
            if (mostRecent) setSelectedGameId(mostRecent.id);
          }
        }
      },
      (err) => console.error('games list error', err)
    );
    return () => unsub();
  }, [gamesColRef]);

  // Sync selected game to localStorage only when it has live: true (so opening an archived game to edit doesn't steal the scoreboard)
  useEffect(() => {
    if (!validTeamId || typeof localStorage === 'undefined') return;
    const key = `gcg-live-game:${teamId}`;
    const selectedGame = games.find((g) => g.id === selectedGameId);
    const isLive = selectedGame?.live !== false; // undefined = true for backwards compat
    if (selectedGameId && isLive) {
      localStorage.setItem(key, selectedGameId);
    } else if (selectedGameId && !isLive) {
      localStorage.setItem(key, '');
    }
  }, [teamId, validTeamId, selectedGameId, games]);

  // --- load team roster (master roster) ---
  useEffect(() => {
    if (!teamRosterRef) return;
    const unsub = onSnapshot(
      teamRosterRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setTeamRoster(data.roster || []);
        } else {
          // If no team roster exists yet, leave empty
          setTeamRoster([]);
        }
      },
      (err) => console.error('team roster error', err)
    );
    return () => unsub();
  }, [teamRosterRef]);

  // --- load one game ---
  // When we apply a remote update we skip the next debounced save and cancel any pending save
  // so multiple StatTracker tabs don't overwrite each other (and the Live Scoreboard).
  useEffect(() => {
    if (!gamesColRef || !selectedGameId) return;
    const gameDocRef = doc(gamesColRef, selectedGameId);
    const unsub = onSnapshot(
      gameDocRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const loadedGameData = data.gameData || {};
          
          // Ensure shotsByPeriod exists for backwards compatibility
          if (!loadedGameData.shotsByPeriod) {
            loadedGameData.shotsByPeriod = { us: {}, them: {} };
          }
          
          // Mark that this update came from Firestore so we don't write it back (multi-tab safety)
          skipNextSaveFromRemoteRef.current = true;
          clearTimeout(saveTimer.current);
          saveTimer.current = null;
          setGameData(loadedGameData);
          setLineup(data.lineup || []);
        }
      },
      (err) => console.error('game data error', err)
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gamesColRef, selectedGameId]);

  // --- debounced save to Firestore (only when user edited in this tab; skip after remote updates) ---
  useEffect(() => {
    if (!gamesColRef || !selectedGameId) return;
    if (skipNextSaveFromRemoteRef.current) {
      skipNextSaveFromRemoteRef.current = false;
      return;
    }
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const sumFromLineup = lineup.filter((p) => p.isGoalie).reduce((s, p) => s + (p.saves || 0), 0);
        const totalGoalieSaves = sumFromLineup > 0 ? sumFromLineup : (gameData.goalieSaves ?? 0);
        const gameDataToSave = { ...gameData, goalieSaves: totalGoalieSaves };
        await setDoc(doc(gamesColRef, selectedGameId), { lineup, gameData: gameDataToSave }, { merge: true });
      } catch (e) {
        console.error('save failed', e);
      }
    }, 350);
    return () => clearTimeout(saveTimer.current);
  }, [lineup, gameData, gamesColRef, selectedGameId]);

  // --- actions ---
  const buildBlankPlayer = (name, number = '', position = '') => {
    const pos = (position || '').trim().toUpperCase();
    const isGoalie = pos === 'G' || pos === 'GOALIE';
    return {
      name: (name || '').trim(),
      number: String(number || '').trim(),
      position: isGoalie ? 'G' : pos,
      goals: 0,
      assists: 0,
      points: 0,
      plusMinus: 0,
      penalties: 0,
      pim: 0,
      shots: 0,
      isGoalie,
      saves: 0,
      goalsAllowed: 0,
      savePercentage: 0,
    };
  };

  const saveMasterRoster = async (roster, successMsg) => {
    if (!teamRosterRef) return false;
    try {
      const cleaned = (roster || []).map(resetPlayerStats);
      await setDoc(teamRosterRef, { roster: cleaned }, { merge: true });
      if (successMsg) setRosterMessage(successMsg);
      return true;
    } catch (e) {
      console.error('Failed to save team roster', e);
      alert(
        e?.code === 'permission-denied'
          ? 'Permission denied saving roster. Deploy latest Firestore rules (statsDev) and confirm your staff account can edit stats.'
          : 'Failed to save team roster. Please try again.'
      );
      return false;
    }
  };

  const handleUpdateTeamRoster = async () => {
    if (!teamRosterRef) return;
    if (lineup.length === 0) {
      alert('No players in the current game lineup to save. Upload a CSV or add players below first.');
      return;
    }

    if (
      window.confirm(
        `Save current game lineup (${lineup.length} players) as the team roster?\n\nThis will be used for all new games.`
      )
    ) {
      const ok = await saveMasterRoster(lineup, 'Team roster updated from current game.');
      if (ok) alert('Team roster updated successfully!');
    }
  };

  const handleAddToMasterRoster = async () => {
    const name = (inputName || '').trim();
    if (!name) {
      alert('Enter a player name.');
      return;
    }
    if (teamRoster.some((p) => (p.name || '').toLowerCase() === name.toLowerCase())) {
      alert(`${name} is already on the team roster.`);
      return;
    }
    const next = [...teamRoster, buildBlankPlayer(name, inputNumber, inputPosition)];
    const ok = await saveMasterRoster(next, `Added ${name} to team roster.`);
    if (ok) {
      setInputName('');
      setInputNumber('');
      setInputPosition('');
    }
  };

  const handleRemoveFromMasterRoster = async (name) => {
    if (!window.confirm(`Remove ${name} from the saved team roster?`)) return;
    const next = teamRoster.filter((p) => p.name !== name);
    await saveMasterRoster(next, `Removed ${name} from team roster.`);
  };

  const handleMasterRosterUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const content = String(evt.target.result || '');
        const lines = content.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
        if (lines.length < 2) {
          setRosterMessage('Error: CSV must include a header and at least one player.');
          return;
        }
        const newPlayers = lines
          .slice(1)
          .map((line) => line.split(',').map((x) => x.trim()))
          .filter((cols) => cols.length >= 1 && cols[0].length > 0)
          .map(([name, number = '', position = '']) => buildBlankPlayer(name, number, position));
        if (newPlayers.length === 0) {
          setRosterMessage('No player rows found.');
          return;
        }
        const ok = await saveMasterRoster(
          newPlayers,
          `Team roster saved (${newPlayers.length} players). You can create a game now.`
        );
        if (ok && selectedGameId && lineup.length === 0) {
          // Seed empty live game with the new master roster
          setLineup(newPlayers.map(resetPlayerStats));
        }
      } catch (err) {
        console.error('CSV parse error', err);
        setRosterMessage('Error parsing file.');
      } finally {
        e.target.value = '';
      }
    };
    reader.readAsText(file);
  };

  const handleCreateNewGame = async () => {
    if (!gamesColRef) return;

    // ✅ Validation: require opponent and location
    const opponent = (newGameDetails.opponent || '').trim();
    const location = (newGameDetails.location || '').trim();

    if (!opponent) {
      alert('Please enter an opponent name before creating a game.');
      return;
    }

    if (!location) {
      alert('Please enter a location before creating a game.');
      return;
    }

    try {
      const newDocRef = doc(gamesColRef);

      // ✅ Use team roster if available, otherwise use current lineup, fallback to empty
      const rosterToUse = teamRoster.length > 0 ? teamRoster : lineup.length > 0 ? lineup : [];

      if (rosterToUse.length === 0) {
        if (
          !window.confirm(
            'No team roster found. Create game with empty roster?\n\nTip: Upload a CSV or add players in Team Roster above first — then new games will start with that roster.'
          )
        ) {
          return;
        }
      }

      // ✅ Reset player stats before saving to Firestore
      const resetLineup = rosterToUse.map(resetPlayerStats);

      await setDoc(newDocRef, {
        details: {
          opponent,
          date: newGameDetails.date || new Date().toISOString().split('T')[0],
          location,
        },
        lineup: resetLineup,
        gameData: {
          score: { us: 0, them: 0 },
          shots: { us: 0, them: 0 },
          shotsByPeriod: { us: {}, them: {} },
          goalieSaves: 0,
          gameLog: [],
          penalties: [],
          currentPeriod: 1,
        },
        archived: false, // ✅ New games are not archived
        live: true, // ✅ New games show on Live Scoreboard by default
      });

      // ✅ update local state so UI shows reset stats immediately
      setLineup(resetLineup);
      setSelectedGameId(newDocRef.id);
      setNewGameDetails({
        opponent: '',
        date: new Date().toISOString().split('T')[0],
        location: '',
      });
    } catch (e) {
      console.error('create game failed', e);
      alert(
        e?.code === 'permission-denied'
          ? 'Permission denied creating game. Deploy latest Firestore rules (statsDev sandbox) and confirm your staff account includes Live scoreboard, Game history, or Gladiators TV.'
          : 'Failed to create game. Check the console and try again.'
      );
    }
  };
  
  // --- archive/unarchive game ---
  const handleArchiveGame = async (gameId, shouldArchive) => {
    if (!gamesColRef) return;
    try {
      await updateDoc(doc(gamesColRef, gameId), {
        archived: shouldArchive
      });
      
      // If we just archived the active game, clear selection
      if (shouldArchive && selectedGameId === gameId) {
        setSelectedGameId(null);
        setGameData({
          score: { us: 0, them: 0 },
          shots: { us: 0, them: 0 },
          shotsByPeriod: { us: {}, them: {} },
          goalieSaves: 0,
          gameLog: [],
          penalties: [],
          currentPeriod: 1,
        });
        setLineup([]);
      }
    } catch (e) {
      console.error('archive game failed', e);
    }
  };
  
  // --- end game with PIN protection ---
  const handleEndGame = async () => {
    if (!selectedGameId || !gamesColRef) return;
    
    const currentGameDetails = games.find(g => g.id === selectedGameId)?.details;
    const opponent = currentGameDetails?.opponent || 'this game';
    
    // Prompt for PIN
    const enteredPin = window.prompt(
      `End game vs. ${opponent}?\n\nThis will archive the game and prevent further edits.\n\nEnter PIN to continue (default: 1234):`,
      ''
    );
    
    // User cancelled
    if (enteredPin === null) return;
    
    // Check PIN (default is "1234", case-insensitive)
    const validPin = '1234';
    if (enteredPin.trim() !== validPin) {
      alert('❌ Incorrect PIN. Game not ended.');
      return;
    }
    
    // Confirm one more time
    if (!window.confirm(`✅ PIN verified.\n\nArchive game vs. ${opponent}?\n\nFinal Score: Gladiators ${gameData.score.us} - ${opponent} ${gameData.score.them}`)) {
      return;
    }
    
    // Archive the game and write to master record (gameHistory)
    const endedAtIso = new Date().toISOString();
    try {
      await updateDoc(doc(gamesColRef, selectedGameId), {
        archived: true,
        endedAt: endedAtIso,
        syncedToMasterAt: endedAtIso
      });
    } catch (e) {
      console.error('end game failed (archive game doc)', e);
      alert(
        '❌ Failed to archive the game document. If this says permission denied, deploy latest Firestore rules and ensure your staff role includes Live scoreboard, Game history, or Gladiators TV.'
      );
      return;
    }
    try {
      if (gameHistoryColRef) {
        const snapshot = buildGameHistorySnapshot(
          selectedGameId,
          currentGameDetails,
          lineup,
          gameData,
          endedAtIso
        );
        await setDoc(doc(gameHistoryColRef, selectedGameId), snapshot, { merge: true });
      }
      setEndGameModal({
        selectedGameId,
        currentGameDetails,
        lineup: [...lineup],
        gameData: { ...gameData },
        endedAtIso,
        opponent,
      });
    } catch (e) {
      console.error('end game failed (game history / master record)', e);
      alert(
        '❌ Game was archived but saving to the master history record failed. Deploy latest Firestore rules (gameHistory uses the same staff gate as the live games). You can use “Sync to master record” after rules are updated.'
      );
    }
  };

  const clearSelectionAndState = () => {
    setSelectedGameId(null);
    setGameData({
      score: { us: 0, them: 0 },
      shots: { us: 0, them: 0 },
      shotsByPeriod: { us: {}, them: {} },
      goalieSaves: 0,
      gameLog: [],
      penalties: [],
      currentPeriod: 1,
    });
    setLineup([]);
    setEndGameModal(null);
    setRecapPhotoFile(null);
    setGladiatorOfGameName('');
    setGladiatorOfGameReason('');
    setGladiatorPhotoFile(null);
    setRecapStyle(DEFAULT_RECAP_STYLE);
    setFanComments([]);
    setSelectedFanCommentId('');
    setRecapProgressStatus('');
  };

  // Load fan comments when End Game modal opens for a game
  useEffect(() => {
    if (!endGameModal?.selectedGameId || !validTeamId) {
      setFanComments([]);
      setSelectedFanCommentId('');
      return undefined;
    }
    const col = collection(db, getStatsCollectionRoot(), teamId, 'games', endGameModal.selectedGameId, 'fanComments');
    const q = query(col, orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setFanComments(list);
      },
      (err) => {
        console.warn('fanComments for end-game modal', err);
        setFanComments([]);
      }
    );
    return () => unsub();
  }, [endGameModal?.selectedGameId, teamId, validTeamId]);

  const handleGenerateRecap = async () => {
    if (!endGameModal || !gamesColRef || !gameHistoryColRef || !validTeamId) return;
    const { selectedGameId: gameId, currentGameDetails, lineup: modalLineup, gameData: modalGameData, endedAtIso } = endGameModal;
    setIsGeneratingRecap(true);
    setRecapProgressStatus('Uploading photos…');
    let imageUrl = null;
    let gladiatorPhotoUrl = null;
    try {
      if (recapPhotoFile) {
        const ext = recapPhotoFile.name.split('.').pop()?.toLowerCase() || 'jpg';
        const safeExt = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext) ? ext : 'jpg';
        const storagePath = `${getStatsCollectionRoot()}/${teamId}/recaps/${gameId}/recap-1.${safeExt}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, recapPhotoFile, recapPhotoUploadMetadata(recapPhotoFile));
        imageUrl = await getDownloadURL(storageRef);
      }
      if (gladiatorPhotoFile) {
        const ext = gladiatorPhotoFile.name.split('.').pop()?.toLowerCase() || 'jpg';
        const safeExt = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext) ? ext : 'jpg';
        const storagePath = `${getStatsCollectionRoot()}/${teamId}/recaps/${gameId}/gladiator.${safeExt}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, gladiatorPhotoFile, recapPhotoUploadMetadata(gladiatorPhotoFile));
        gladiatorPhotoUrl = await getDownloadURL(storageRef);
      }
    } catch (e) {
      console.error('Photo upload failed:', e);
      alert('Photo upload failed. Generating recap without photo(s).');
    }
    const gladiatorOfGame =
      gladiatorOfGameName?.trim() && gladiatorOfGameReason?.trim()
        ? {
            playerName: gladiatorOfGameName.trim(),
            reason: gladiatorOfGameReason.trim(),
            ...(gladiatorPhotoUrl ? { photoUrl: gladiatorPhotoUrl } : {}),
          }
        : null;

    const selectedFan = fanComments.find((c) => c.id === selectedFanCommentId);
    const fanQuote =
      selectedFan?.displayName?.trim() && selectedFan?.text?.trim()
        ? {
            displayName: selectedFan.displayName.trim(),
            text: selectedFan.text.trim(),
          }
        : null;

    let recap = null;
    setRecapProgressStatus('Writing the Gladiator Gazette… this can take a minute');
    try {
      const generateRecap = httpsCallable(functions, 'generateGameRecap');
      const payload = {
        teamId,
        teamLabel: TEAM_STATS_TEAMS.find((t) => t.teamId === teamId)?.label,
        details: currentGameDetails,
        lineup: modalLineup,
        gameData: modalGameData,
        endedAtIso,
        gladiatorOfGame: gladiatorOfGame || undefined,
        fanQuote: fanQuote || undefined,
        recapStyle,
      };
      const result = await generateRecap(payload);
      if (result?.data?.headline != null && result?.data?.body != null) {
        recap = { headline: result.data.headline, body: result.data.body };
      }
    } catch (e) {
      console.warn('AI recap failed, using template:', e);
    }
    if (!recap) {
      setRecapProgressStatus('Using backup template…');
      const templateRecap = buildGameRecap(currentGameDetails, modalLineup, modalGameData, endedAtIso, {
        recapStyle,
        fanQuote: fanQuote || undefined,
      });
      recap = { headline: templateRecap.headline, body: templateRecap.body };
    }
    setRecapProgressStatus('Saving Gazette…');
    try {
      const recapPayload = {
        ...recap,
        generatedAt: endedAtIso,
        recapStyle,
        ...(imageUrl ? { images: [imageUrl] } : {}),
        ...(gladiatorOfGame ? { gladiatorOfGame } : {}),
        ...(fanQuote ? { fanQuote } : {}),
      };
      await updateDoc(doc(gamesColRef, gameId), { recap: recapPayload });
      const snapshotWithRecap = buildGameHistorySnapshot(gameId, currentGameDetails, modalLineup, modalGameData, endedAtIso, recapPayload);
      await setDoc(doc(gameHistoryColRef, gameId), snapshotWithRecap, { merge: true });
      alert('Recap generated! View it on the Live Scoreboard, Gladiators TV, and Gladiator Gazette.');
      clearSelectionAndState();
    } catch (e) {
      console.error('generate recap failed', e);
      alert('Failed to save recap. Please try again.');
    } finally {
      setIsGeneratingRecap(false);
      setRecapProgressStatus('');
    }
  };

  const handleEndGameModalDone = () => {
    clearSelectionAndState();
  };

  /** Re-archive game (e.g. after unarchiving to edit) and update master record. No PIN. */
  const handleReArchiveGame = async () => {
    if (!selectedGameId || !gamesColRef) return;
    const currentGameDetails = games.find(g => g.id === selectedGameId)?.details;
    const opponent = currentGameDetails?.opponent || 'this game';
    if (!window.confirm(`Re-archive this game vs. ${opponent}?\n\nCurrent stats will be saved to the master record and the game will be locked again.`)) return;
    const endedAtIso = new Date().toISOString();
    try {
      await updateDoc(doc(gamesColRef, selectedGameId), {
        archived: true,
        endedAt: endedAtIso,
        syncedToMasterAt: endedAtIso
      });
      if (gameHistoryColRef) {
        const snapshot = buildGameHistorySnapshot(
          selectedGameId,
          currentGameDetails,
          lineup,
          gameData,
          endedAtIso
        );
        await setDoc(doc(gameHistoryColRef, selectedGameId), snapshot, { merge: true });
      }
      setEndGameModal({
        selectedGameId,
        currentGameDetails,
        lineup: [...lineup],
        gameData: { ...gameData },
        endedAtIso,
        opponent,
      });
    } catch (e) {
      console.error('re-archive failed', e);
      alert('❌ Failed to re-archive. Please try again.');
    }
  };

  /** Sync all archived games into the master record (gameHistory). Use for retroactive backfill. */
  const handleBackfillGameHistory = async () => {
    if (!gamesColRef || !gameHistoryColRef) return;
    if (!window.confirm('Sync all archived games into the master record?\n\nThis will add or update each archived game in the game history. Safe to run multiple times.')) return;
    setIsBackfillingHistory(true);
    try {
      const q = query(gamesColRef, where('archived', '==', true));
      const snap = await getDocs(q);
      let count = 0;
      for (const d of snap.docs) {
        const data = d.data();
        const snapshot = buildGameHistorySnapshot(
          d.id,
          data.details,
          data.lineup,
          data.gameData,
          data.endedAt || null
        );
        await setDoc(doc(gameHistoryColRef, d.id), snapshot, { merge: true });
        await updateDoc(doc(gamesColRef, d.id), {
          syncedToMasterAt: data.endedAt || new Date().toISOString()
        });
        count += 1;
      }
      alert(`✅ Master record updated.\n\n${count} archived game(s) synced to game history.`);
    } catch (e) {
      console.error('backfill game history failed', e);
      alert('❌ Failed to sync archived games. Please try again.');
    } finally {
      setIsBackfillingHistory(false);
    }
  };
  
  // --- delete game ---
  const handleDeleteGame = async (gameId) => {
    if (!gamesColRef) return;
    try {
      await deleteDoc(doc(gamesColRef, gameId));
  
      // if you delete the active game, clear UI state
      if (selectedGameId === gameId) {
        setSelectedGameId(null);
        setGameData({
          score: { us: 0, them: 0 },
          shots: { us: 0, them: 0 },
          shotsByPeriod: { us: {}, them: {} },
          goalieSaves: 0,
          gameLog: [],
          penalties: [],
          currentPeriod: 1,
        });
        setLineup([]);
      }
    } catch (e) {
      console.error('delete game failed', e);
    }
  };  
  

  const handleAddPlayer = () => {
    const name = (inputName || '').trim();
    if (!name) return;
    
    const number = (inputNumber || '').trim();
    const position = (inputPosition || '').trim().toUpperCase();
    const isGoalie = position === 'G' || position === 'GOALIE';
    
    setLineup((prev) => [
      ...prev,
      {
        name,
        number,
        position: isGoalie ? 'G' : position,
        goals: 0,
        assists: 0,
        points: 0,
        plusMinus: 0,
        penalties: 0,
        pim: 0,
        shots: 0,
        isGoalie,
        // ✅ goalie stats (default to 0)
        saves: 0,
        goalsAllowed: 0,
        savePercentage: 0
      },
    ]);
    setInputName('');
    setInputNumber('');
    setInputPosition('');
    setIsRosterVisible(false);
  };
  
  const handleEditPlayer = (p) => {
    setEditingPlayerName(p.name);
    setNewPlayerName(p.name);
    setNewPlayerNumber(String(p.number ?? ''));
    setNewPlayerPosition(p.position || '');
  };
  const handleSavePlayerEdit = () => {
    const newName = (newPlayerName || '').trim();
    if (!newName) { setEditingPlayerName(null); setNewPlayerName(''); setNewPlayerNumber(''); setNewPlayerPosition(''); return; }
    const number = (newPlayerNumber || '').trim();
    const position = (newPlayerPosition || '').trim().toUpperCase();
    const isGoalie = position === 'G' || position === 'GOALIE';
    setLineup((prev) => prev.map((player) =>
      player.name === editingPlayerName
        ? { ...player, name: newName, number, position: isGoalie ? 'G' : position, isGoalie }
        : player
    ));
    setGameData((prev) => {
      const log = prev.gameLog.map((e) => {
        const x = { ...e };
        ['scorer', 'assist1', 'assist2', 'assist3'].forEach((k) => { if (x[k] === editingPlayerName) x[k] = newName; });
        return x;
      });
      const pens = prev.penalties.map((pen) => (pen.player === editingPlayerName ? { ...pen, player: newName } : pen));
      return { ...prev, gameLog: log, penalties: pens };
    });
    setEditingPlayerName(null);
    setNewPlayerName('');
    setNewPlayerNumber('');
    setNewPlayerPosition('');
  };
  const handleCancelPlayerEdit = () => {
    setEditingPlayerName(null);
    setNewPlayerName('');
    setNewPlayerNumber('');
    setNewPlayerPosition('');
  };

  /** Toggle a player as goalie (allow multiple goalies). Clearing goalie clears active if it was them. */
  const handleSetGoalie = (name) => {
    setLineup((prev) =>
      prev.map((p) => ({
        ...p,
        isGoalie: p.name === name ? !p.isGoalie : p.isGoalie,
      }))
    );
    if (activeGoalieName === name) setActiveGoalieName(null);
  };

  /** Set which goalie is "in net" for attributing saves/goals allowed. Only one active at a time. */
  const handleSetActiveGoalie = (name) => {
    setActiveGoalieName((current) => (current === name ? null : name));
  };

  const handleRemovePlayer = (name) => {
    if (window.confirm(`Are you sure you want to remove ${name} from the roster?\n\nNote: This will NOT delete their statistics from previous games or the game log.`)) {
      if (activeGoalieName === name) setActiveGoalieName(null);
      setLineup((prev) => prev.filter((p) => p.name !== name));
    }
  };
  
  const resetPlayerStats = (player) => ({
    ...player,
    goals: 0,
    assists: 0,
    points: 0,
    plusMinus: 0,
    penalties: 0,
    pim: 0,
    shots: 0,          // ✅ reset skater shots
    saves: 0,          // ✅ reset goalie saves
    goalsAllowed: 0,   // ✅ reset goals allowed
    savePercentage: 0  // ✅ reset save %
  });
  
  const handlePlusMinusChange = (name, delta) => {
    setLineup((prev) =>
      prev.map((p) =>
        p.name === name ? { ...p, plusMinus: (p.plusMinus || 0) + delta } : p
      )
    );
  };
  
  const handleShotUs = () => setGameData((prev) => ({ 
    ...prev, 
    shots: { ...prev.shots, us: prev.shots.us + 1 },
    shotsByPeriod: {
      ...prev.shotsByPeriod,
      us: { ...prev.shotsByPeriod.us, [prev.currentPeriod]: (prev.shotsByPeriod.us[prev.currentPeriod] || 0) + 1 }
    }
  }));
  
  const handleRemoveShotUs = () => setGameData((prev) => ({ 
    ...prev, 
    shots: { ...prev.shots, us: Math.max(0, prev.shots.us - 1) },
    shotsByPeriod: {
      ...prev.shotsByPeriod,
      us: { ...prev.shotsByPeriod.us, [prev.currentPeriod]: Math.max(0, (prev.shotsByPeriod.us[prev.currentPeriod] || 0) - 1) }
    }
  }));
  
  const handleShotThem = () => {
    // Opponent SOG that is stopped → count as a save for the active goalie (and a shot against).
    handleAddSave();
  };

  const handleRemoveShotThem = () => {
    // Undo opponent SOG: prefer undoing a save; otherwise just drop the shot total
    // (e.g. after goals that added shots without saves).
    if (activeGoalieName) {
      const activeGoalie = lineup.find((p) => p.name === activeGoalieName && p.isGoalie);
      if (activeGoalie && (activeGoalie.saves || 0) > 0) {
        handleRemoveSave();
        return;
      }
    }
    setGameData((prev) => ({
      ...prev,
      shots: { ...prev.shots, them: Math.max(0, prev.shots.them - 1) },
      shotsByPeriod: {
        ...prev.shotsByPeriod,
        them: {
          ...prev.shotsByPeriod.them,
          [prev.currentPeriod]: Math.max(0, (prev.shotsByPeriod.them[prev.currentPeriod] || 0) - 1),
        },
      },
    }));
  };
  
  const handleAddOurGoal = () => {
    // Validate that a scorer is selected
    if (!scorerName || scorerName.trim() === '') {
      alert('⚠️ Please select a goal scorer before logging a goal.');
      return;
    }
    
    setGameData((prev) => ({
      ...prev,
      score: { ...prev.score, us: prev.score.us + 1 },
      shots: { ...prev.shots, us: prev.shots.us + 1 },
      shotsByPeriod: {
        ...prev.shotsByPeriod,
        us: { ...prev.shotsByPeriod.us, [prev.currentPeriod]: (prev.shotsByPeriod.us[prev.currentPeriod] || 0) + 1 }
      },
      gameLog: [
        ...prev.gameLog,
        {
          type: 'goal_us',
          period: prev.currentPeriod,
          scorer: scorerName,
          assist1: assist1Name,
          assist2: assist2Name,
          assist3: assist3Name,
          time: new Date().toLocaleTimeString(),
        },
      ],
    }));
    setLineup((prev) =>
      prev.map((p) => {
        const u = { ...p };
        if (p.name === scorerName) { u.goals += 1; u.points += 1; u.shots += 1; }
        [assist1Name, assist2Name, assist3Name].forEach((a) => {
          if (p.name === a) { u.assists += 1; u.points += 1; }
        });
        return u;
      })
    );
    setScorerName('');
    setAssist1Name('');
    setAssist2Name('');
    setAssist3Name('');
  };
  
  const handleRemoveOurGoal = () => {
    if (gameData.score.us === 0) return;
    
    const lastGoalIndex = gameData.gameLog.map((e, i) => e.type === 'goal_us' ? i : -1)
      .filter(i => i !== -1).pop();
    
    if (lastGoalIndex === undefined) return;
    
    const lastGoal = gameData.gameLog[lastGoalIndex];
    const goalPeriod = lastGoal.period || gameData.currentPeriod;
    
    setGameData((prev) => ({
      ...prev,
      score: { ...prev.score, us: Math.max(0, prev.score.us - 1) },
      shots: { ...prev.shots, us: Math.max(0, prev.shots.us - 1) },
      shotsByPeriod: {
        ...prev.shotsByPeriod,
        us: { ...prev.shotsByPeriod.us, [goalPeriod]: Math.max(0, (prev.shotsByPeriod.us[goalPeriod] || 0) - 1) }
      },
      gameLog: prev.gameLog.filter((_, i) => i !== lastGoalIndex),
    }));
    
    setLineup((prev) =>
      prev.map((p) => {
        const u = { ...p };
        if (p.name === lastGoal.scorer) {
          u.goals = Math.max(0, u.goals - 1);
          u.points = Math.max(0, u.points - 1);
          u.shots = Math.max(0, u.shots - 1);
        }
        [lastGoal.assist1, lastGoal.assist2, lastGoal.assist3].forEach((a) => {
          if (a && p.name === a) {
            u.assists = Math.max(0, u.assists - 1);
            u.points = Math.max(0, u.points - 1);
          }
        });
        return u;
      })
    );
  };
  
  const handleAddOpponentGoal = () => {
    if (!activeGoalieName) {
      alert(
        'Activate a goalie first (Goalies → Activate). Opponent goals count as a shot against and a goal allowed for the goalie in net.'
      );
      return;
    }
    setGameData((prev) => ({
      ...prev,
      score: { ...prev.score, them: prev.score.them + 1 },
      shots: { ...prev.shots, them: prev.shots.them + 1 },
      shotsByPeriod: {
        ...prev.shotsByPeriod,
        them: { ...prev.shotsByPeriod.them, [prev.currentPeriod]: (prev.shotsByPeriod.them[prev.currentPeriod] || 0) + 1 },
      },
      gameLog: [
        ...prev.gameLog,
        {
          type: 'goal_them',
          period: prev.currentPeriod,
          time: new Date().toLocaleTimeString(),
          goalieAllowed: activeGoalieName,
        },
      ],
    }));
    setLineup((prev) =>
      prev.map((p) =>
        p.name === activeGoalieName
          ? { ...p, goalsAllowed: (p.goalsAllowed || 0) + 1 }
          : p
      )
    );
  };

  const handleRemoveOpponentGoal = () => {
    if (gameData.score.them === 0) return;
    const lastOppGoalIndex = gameData.gameLog.map((e, i) => e.type === 'goal_them' ? i : -1)
      .filter((i) => i !== -1).pop();
    if (lastOppGoalIndex === undefined) return;
    const lastGoal = gameData.gameLog[lastOppGoalIndex];
    const goalieAllowed = lastGoal.goalieAllowed || null;
    const goalPeriod = lastGoal.period || gameData.currentPeriod;
    setGameData((prev) => ({
      ...prev,
      score: { ...prev.score, them: Math.max(0, prev.score.them - 1) },
      shots: { ...prev.shots, them: Math.max(0, prev.shots.them - 1) },
      shotsByPeriod: {
        ...prev.shotsByPeriod,
        them: { ...prev.shotsByPeriod.them, [goalPeriod]: Math.max(0, (prev.shotsByPeriod.them[goalPeriod] || 0) - 1) }
      },
      gameLog: prev.gameLog.filter((_, i) => i !== lastOppGoalIndex),
    }));
    if (goalieAllowed) {
      setLineup((prev) =>
        prev.map((p) =>
          p.name === goalieAllowed && (p.goalsAllowed || 0) > 0
            ? { ...p, goalsAllowed: Math.max(0, (p.goalsAllowed || 0) - 1) }
            : p
        )
      );
    }
  };

  const handleAddSave = () => {
    if (!activeGoalieName) {
      alert('Select an active goalie in the Goalies section first (click Activate next to the goalie in net).');
      return;
    }
    setLineup((prev) =>
      prev.map((p) =>
        p.name === activeGoalieName
          ? { ...p, saves: (p.saves || 0) + 1 }
          : p
      )
    );
    setGameData((prev) => ({
      ...prev,
      goalieSaves: prev.goalieSaves + 1,
      shots: { ...prev.shots, them: prev.shots.them + 1 },
      shotsByPeriod: {
        ...prev.shotsByPeriod,
        them: { ...prev.shotsByPeriod.them, [prev.currentPeriod]: (prev.shotsByPeriod.them[prev.currentPeriod] || 0) + 1 },
      },
    }));
  };

  const handleRemoveSave = () => {
    if (!activeGoalieName) {
      alert('Select an active goalie first to remove a save from.');
      return;
    }
    const activeGoalie = lineup.find((p) => p.name === activeGoalieName && p.isGoalie);
    if (!activeGoalie || (activeGoalie.saves || 0) === 0) {
      alert('Active goalie has no saves to remove.');
      return;
    }
    setLineup((prev) =>
      prev.map((p) =>
        p.name === activeGoalieName
          ? { ...p, saves: Math.max(0, (p.saves || 0) - 1) }
          : p
      )
    );
    setGameData((prev) => ({
      ...prev,
      goalieSaves: Math.max(0, prev.goalieSaves - 1),
      shots: { ...prev.shots, them: Math.max(0, prev.shots.them - 1) },
      shotsByPeriod: {
        ...prev.shotsByPeriod,
        them: {
          ...prev.shotsByPeriod.them,
          [prev.currentPeriod]: Math.max(0, (prev.shotsByPeriod.them[prev.currentPeriod] || 0) - 1),
        },
      },
    }));
  };

  const convertToMinutes = (mmss) => {
    const [m, s] = (mmss || '0:00').split(':').map((n) => parseInt(n || '0', 10));
    return (isNaN(m) ? 0 : m) + (isNaN(s) ? 0 : s) / 60;
  };

  const handleAddPenalty = () => {
    const duration = useCustomPenalty
      ? `${String(parseInt(customPenaltyMins || '0', 10))}:${String(
          Math.min(59, parseInt(customPenaltySecs || '0', 10))
        ).padStart(2, '0')}`
      : penaltyDuration;
  
    if (!penaltyPlayer || !penaltyType || !duration.includes(':')) return;
  
    const pimValue = convertToMinutes(duration);
    const timestamp = new Date().toLocaleTimeString();
  
    setGameData((prev) => ({
      ...prev,
      penalties: [
        ...prev.penalties,
        {
          player: penaltyPlayer,
          type: penaltyType,
          duration,
          period: prev.currentPeriod,
          time: timestamp,
        },
      ],
      gameLog: [
        ...prev.gameLog,
        {
          type: 'penalty',
          player: penaltyPlayer,
          penaltyType,
          duration,
          period: prev.currentPeriod,
          time: timestamp,
        },
      ],
    }));
  
    setLineup((prev) =>
      prev.map((p) =>
        p.name === penaltyPlayer
          ? { ...p, penalties: p.penalties + 1, pim: p.pim + pimValue }
          : p
      )
    );
  
    setPenaltyPlayer('');
    setPenaltyType('');
    setPenaltyDuration('1:30');
    setUseCustomPenalty(false);
    setCustomPenaltyMins('');
    setCustomPenaltySecs('');
  };

  const handleStartEditLogEntry = (i) => {
    const e = gameData.gameLog[i];
    if (!e) return;
    if (e.type === 'goal_us') {
      setEditingLogIndex(i);
      setEditDraft({
        type: 'goal_us',
        scorer: e.scorer || '',
        assist1: e.assist1 || '',
        assist2: e.assist2 || '',
        assist3: e.assist3 || '',
      });
    } else if (e.type === 'penalty') {
      setEditingLogIndex(i);
      setEditDraft({
        type: 'penalty',
        player: e.player || '',
        penaltyType: e.penaltyType || '',
        duration: e.duration || '1:30',
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingLogIndex(null);
    setEditDraft(null);
  };

  const handleSaveEditLogEntry = () => {
    if (editingLogIndex == null || !editDraft) return;
    const entry = gameData.gameLog[editingLogIndex];
    if (!entry) { handleCancelEdit(); return; }

    if (editDraft.type === 'goal_us') {
      const { scorer: newScorer, assist1: newA1, assist2: newA2, assist3: newA3 } = editDraft;
      if (!(newScorer && newScorer.trim())) {
        alert('Please select a goal scorer.');
        return;
      }
      const oldScorer = entry.scorer || '';
      const oldA1 = entry.assist1 || '';
      const oldA2 = entry.assist2 || '';
      const oldA3 = entry.assist3 || '';

      setLineup((prev) =>
        prev.map((p) => {
          const u = { ...p };
          if (p.name === oldScorer) {
            u.goals = Math.max(0, u.goals - 1);
            u.points = Math.max(0, u.points - 1);
            u.shots = Math.max(0, u.shots - 1);
          }
          [oldA1, oldA2, oldA3].forEach((a) => {
            if (a && p.name === a) {
              u.assists = Math.max(0, u.assists - 1);
              u.points = Math.max(0, u.points - 1);
            }
          });
          if (p.name === newScorer) {
            u.goals += 1;
            u.points += 1;
            u.shots += 1;
          }
          [newA1, newA2, newA3].forEach((a) => {
            if (a && p.name === a) {
              u.assists += 1;
              u.points += 1;
            }
          });
          return u;
        })
      );
      setGameData((prev) => ({
        ...prev,
        gameLog: prev.gameLog.map((item, idx) =>
          idx === editingLogIndex
            ? { ...entry, scorer: newScorer.trim(), assist1: (newA1 || '').trim(), assist2: (newA2 || '').trim(), assist3: (newA3 || '').trim() }
            : item
        ),
      }));
    } else if (editDraft.type === 'penalty') {
      const { player: newPlayer, penaltyType: newType, duration: newDuration } = editDraft;
      if (!(newPlayer && newPlayer.trim()) || !(newType && newType.trim()) || !(newDuration && newDuration.includes(':'))) {
        alert('Please select player, type, and duration.');
        return;
      }
      const oldPlayer = entry.player || '';
      const oldDuration = entry.duration || '0:00';
      const oldPim = convertToMinutes(oldDuration);
      const newPim = convertToMinutes(newDuration);
      const penaltyIndex = gameData.gameLog.slice(0, editingLogIndex).filter((x) => x.type === 'penalty').length;

      setLineup((prev) =>
        prev.map((p) => {
          const u = { ...p };
          if (p.name === oldPlayer) {
            u.penalties = Math.max(0, u.penalties - 1);
            u.pim = Math.max(0, (u.pim || 0) - oldPim);
          }
          if (p.name === newPlayer) {
            u.penalties = (u.penalties || 0) + 1;
            u.pim = (u.pim || 0) + newPim;
          }
          return u;
        })
      );
      setGameData((prev) => {
        const newPenalties = [...prev.penalties];
        if (newPenalties[penaltyIndex] != null) {
          newPenalties[penaltyIndex] = {
            ...newPenalties[penaltyIndex],
            player: newPlayer.trim(),
            type: newType.trim(),
            duration: newDuration.trim(),
          };
        }
        return {
          ...prev,
          penalties: newPenalties,
          gameLog: prev.gameLog.map((item, idx) =>
            idx === editingLogIndex
              ? { ...entry, player: newPlayer.trim(), penaltyType: newType.trim(), duration: newDuration.trim() }
              : item
          ),
        };
      });
    }

    setEditingLogIndex(null);
    setEditDraft(null);
  };

  const handleRemoveLogEntry = (i) => {
    const entry = gameData.gameLog[i];
    if (!entry) return;
    const entryType = entry.type;
    if (entryType !== 'goal_us' && entryType !== 'goal_them' && entryType !== 'penalty') return;
    const confirmMsg =
      entryType === 'goal_us'
        ? 'Remove this goal from the game log? Score and player stats will be updated.'
        : entryType === 'goal_them'
          ? 'Remove this opponent goal from the game log? Score and shots will be updated.'
          : 'Remove this penalty from the game log?';
    if (!window.confirm(confirmMsg)) return;

    if (editingLogIndex === i) {
      setEditingLogIndex(null);
      setEditDraft(null);
    }

    if (entryType === 'goal_us') {
      const goalPeriod = entry.period || gameData.currentPeriod;
      setGameData((prev) => ({
        ...prev,
        score: { ...prev.score, us: Math.max(0, prev.score.us - 1) },
        shots: { ...prev.shots, us: Math.max(0, prev.shots.us - 1) },
        shotsByPeriod: {
          ...prev.shotsByPeriod,
          us: { ...prev.shotsByPeriod.us, [goalPeriod]: Math.max(0, (prev.shotsByPeriod.us[goalPeriod] || 0) - 1) }
        },
        gameLog: prev.gameLog.filter((_, idx) => idx !== i),
      }));
      setLineup((prev) =>
        prev.map((p) => {
          const u = { ...p };
          if (p.name === entry.scorer) {
            u.goals = Math.max(0, u.goals - 1);
            u.points = Math.max(0, u.points - 1);
            u.shots = Math.max(0, u.shots - 1);
          }
          [entry.assist1, entry.assist2, entry.assist3].forEach((a) => {
            if (a && p.name === a) {
              u.assists = Math.max(0, u.assists - 1);
              u.points = Math.max(0, u.points - 1);
            }
          });
          return u;
        })
      );
    } else if (entryType === 'goal_them') {
      const goalPeriod = entry.period || gameData.currentPeriod;
      setGameData((prev) => ({
        ...prev,
        score: { ...prev.score, them: Math.max(0, prev.score.them - 1) },
        shots: { ...prev.shots, them: Math.max(0, prev.shots.them - 1) },
        shotsByPeriod: {
          ...prev.shotsByPeriod,
          them: { ...prev.shotsByPeriod.them, [goalPeriod]: Math.max(0, (prev.shotsByPeriod.them[goalPeriod] || 0) - 1) }
        },
        gameLog: prev.gameLog.filter((_, idx) => idx !== i),
      }));
    } else if (entryType === 'penalty') {
      const penaltyIndex = gameData.gameLog.slice(0, i).filter((x) => x.type === 'penalty').length;
      const pimValue = convertToMinutes(entry.duration || '0:00');
      setGameData((prev) => ({
        ...prev,
        penalties: prev.penalties.filter((_, idx) => idx !== penaltyIndex),
        gameLog: prev.gameLog.filter((_, idx) => idx !== i),
      }));
      setLineup((prev) =>
        prev.map((p) =>
          p.name === entry.player
            ? {
                ...p,
                penalties: Math.max(0, (p.penalties || 0) - 1),
                pim: Math.max(0, (p.pim || 0) - pimValue),
              }
            : p
        )
      );
    }
  };

  const handleRosterUpload = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const content = String(evt.target.result || '');
        const lines = content.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
        if (lines.length < 2) { setRosterMessage('Error: CSV must include a header and at least one player.'); return; }
        const newPlayers = lines.slice(1)
          .map((line) => line.split(',').map((x) => x.trim()))
          .filter((cols) => cols.length >= 1 && cols[0].length > 0)
          .map(([name, number = '', position = '']) => {
            const isGoalie = ['g', 'goalie'].includes(position.toLowerCase());
            return { name, number, position, goals: 0, assists: 0, points: 0, isGoalie, shots: 0, penalties: 0, pim: 0, plusMinus: 0 };
          });
        if (newPlayers.length === 0) { setRosterMessage('No player rows found.'); return; }
        setLineup(newPlayers); setRosterMessage('Roster uploaded successfully.'); setIsRosterVisible(false);
      } catch (err) { console.error('CSV parse error', err); setRosterMessage('Error parsing file.'); }
    };
    reader.readAsText(file);
  };

  const penaltyOptions = useMemo(() => { const opts = []; for (let t = 90; t <= 600; t += 30) { const m = Math.floor(t / 60), s = t % 60; opts.push(`${m}:${String(s).padStart(2, '0')}`); } return opts; }, []);
  const penaltyTypes = ['Boarding','Charging','Clipping','Cross-checking','Elbowing','High-sticking','Holding','Hooking','Interference','Kneeing','Roughing','Slashing','Tripping','Unsportsmanlike Conduct','Too Many Men on the Ice'];
  const handlePeriodChange = (d) => setGameData((prev) => ({ ...prev, currentPeriod: Math.max(1, prev.currentPeriod + d) }));

  const goalies = useMemo(() => lineup.filter((p) => p.isGoalie), [lineup]);
  const activeGoalie = goalies.find((p) => p.name === activeGoalieName);
  const totalGoalieSaves = useMemo(
    () => goalies.reduce((s, p) => s + (p.saves || 0), 0),
    [goalies]
  );
  const shotsAgainst = (gameData.goalieSaves ?? totalGoalieSaves) + gameData.score.them;
  const savePctCombined = shotsAgainst > 0 ? ((gameData.goalieSaves ?? totalGoalieSaves) / shotsAgainst) * 100 : 0;

  const skaters = useMemo(() => lineup.filter((p) => !p.isGoalie), [lineup]);
  const selectedPlayer = useMemo(
    () => (selectedSkaterName && selectedSkaterName !== '__all__' ? lineup.find((p) => p.name === selectedSkaterName) : null),
    [lineup, selectedSkaterName]
  );
  const playerEvents = useMemo(() => {
    if (!selectedPlayer?.name) return [];
    const name = selectedPlayer.name;
    return (gameData.gameLog || []).map((e, i) => {
      if (e.type === 'goal_us') {
        if (e.scorer === name) return { ...e, index: i, kind: 'goal', label: 'Goal' };
        if ([e.assist1, e.assist2, e.assist3].includes(name)) return { ...e, index: i, kind: 'assist', label: 'Assist' };
      }
      if (e.type === 'penalty' && e.player === name) return { ...e, index: i, kind: 'penalty', label: `Penalty — ${e.penaltyType || ''} (${e.duration || ''})` };
      return null;
    }).filter(Boolean);
  }, [gameData.gameLog, selectedPlayer]);
  const currentGame = games.find((g) => g.id === selectedGameId);
  
  // ✅ Sort roster based on selected option
  const getSortedLineup = () => {
    const sorted = [...lineup];
    
    switch (rosterSortBy) {
      case 'firstName':
        return sorted.sort((a, b) => {
          const aFirst = a.name.split(' ')[0].toLowerCase();
          const bFirst = b.name.split(' ')[0].toLowerCase();
          return aFirst.localeCompare(bFirst);
        });
      
      case 'lastName':
        return sorted.sort((a, b) => {
          const aParts = a.name.split(' ');
          const bParts = b.name.split(' ');
          const aLast = (aParts.length > 1 ? aParts[aParts.length - 1] : aParts[0]).toLowerCase();
          const bLast = (bParts.length > 1 ? bParts[bParts.length - 1] : bParts[0]).toLowerCase();
          return aLast.localeCompare(bLast);
        });
      
      case 'number':
        return sorted.sort((a, b) => {
          const aNum = parseInt(a.number || '999', 10);
          const bNum = parseInt(b.number || '999', 10);
          return aNum - bNum;
        });
      
      case 'plusMinus':
        return sorted.sort((a, b) => (b.plusMinus || 0) - (a.plusMinus || 0));
      
      default: // 'default'
        return sorted;
    }
  };
  
  const sortedLineup = getSortedLineup();
  const teamAgeLabel = TEAM_STATS_TEAMS.find((t) => t.teamId === teamId)?.label;

  // ✅ Filter games by archived status
  const activeGames = games.filter(g => !g.archived);
  const archivedGames = games.filter(g => g.archived);
  const displayedGames = showArchivedGames ? archivedGames : activeGames;

  if (!validTeamId) return <div className="container py-4 text-danger">Missing required prop: teamId</div>;
  if (loading) return <div className="container py-5 text-center text-light">Loading…</div>;

  return (
    <div className="bg-app">
      {/* Post-end game modal: Generate recap or Done */}
      {endGameModal && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3" style={{ zIndex: 1050, background: 'rgba(0,0,0,0.6)' }}>
          <div className="card card-glad shadow-lg position-relative" style={{ maxWidth: 480, width: '100%' }}>
            {isGeneratingRecap && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center p-4"
                style={{ zIndex: 10, background: 'rgba(7,13,17,0.92)', borderRadius: 'inherit' }}
                role="status"
                aria-live="polite"
              >
                <div className="spinner-border text-light mb-3" role="presentation" />
                <p className="text-light text-center mb-1 fw-semibold">
                  {recapProgressStatus || 'Working on the Gladiator Gazette…'}
                </p>
                <p className="small text-muted-brand text-center mb-0">
                  Please keep this window open. Generation can take up to a minute.
                </p>
              </div>
            )}
            <div className="card-body p-4">
              <h3 className="h5 mb-3" style={{ color: 'var(--brand-gold)' }}>Game ended</h3>
              <p className="text-light mb-3">
                Final score: Gladiators {endGameModal.gameData?.score?.us ?? 0} – {endGameModal.opponent || 'Opponent'} {endGameModal.gameData?.score?.them ?? 0}. The game has been archived and saved to the master record.
              </p>
              <p className="small text-muted-brand mb-3">Add optional details for the recap, then generate the story.</p>

              <div className="mb-3">
                <label className="form-label small text-muted-brand mb-1">Gazette style</label>
                <select
                  className="form-select form-select-sm"
                  value={recapStyle}
                  onChange={(e) => setRecapStyle(e.target.value)}
                  disabled={isGeneratingRecap}
                >
                  {RECAP_STYLE_OPTIONS.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="small text-muted-brand mt-1 mb-0">
                  Changes tone and how quotes read; game stats are unchanged.
                </p>
              </div>

              {/* Family fan quote from Gladiators TV */}
              <div className="mb-3">
                <label className="form-label small text-muted-brand mb-1">Family quote from Gladiators TV (optional)</label>
                {fanComments.length === 0 ? (
                  <p className="small text-muted-brand mb-0">No family quotes were submitted during this game.</p>
                ) : (
                  <select
                    className="form-select form-select-sm"
                    value={selectedFanCommentId}
                    onChange={(e) => setSelectedFanCommentId(e.target.value)}
                    disabled={isGeneratingRecap}
                  >
                    <option value="">— Skip / none —</option>
                    {fanComments.map((c) => (
                      <option key={c.id} value={c.id}>
                        {(c.displayName || 'Fan').slice(0, 24)}: {(c.text || '').slice(0, 60)}
                        {(c.text || '').length > 60 ? '…' : ''}
                      </option>
                    ))}
                  </select>
                )}
                <p className="small text-muted-brand mt-1 mb-0">
                  Pick one quote to feature in the Gazette as “From the crowd.”
                </p>
              </div>

              {/* 1) Photo from the game */}
              <div className="mb-3">
                <label className="form-label small text-muted-brand mb-1">1. Photo from the game (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  className="form-control form-control-sm"
                  onChange={(e) => setRecapPhotoFile(e.target.files?.[0] || null)}
                  disabled={isGeneratingRecap}
                />
                {recapPhotoFile && <span className="small text-muted-brand ms-2">{recapPhotoFile.name}</span>}
              </div>

              {/* 2) Photo of Gladiator of the Game */}
              <div className="mb-3">
                <label className="form-label small text-muted-brand mb-1">2. Photo of Gladiator of the Game (optional, small in story)</label>
                <input
                  type="file"
                  accept="image/*"
                  className="form-control form-control-sm"
                  onChange={(e) => setGladiatorPhotoFile(e.target.files?.[0] || null)}
                  disabled={isGeneratingRecap}
                />
                {gladiatorPhotoFile && <span className="small text-muted-brand ms-2">{gladiatorPhotoFile.name}</span>}
              </div>

              {/* Gladiator of the Game */}
              <div className="mb-3">
                <label className="form-label small text-muted-brand mb-1">Gladiator of the Game (optional)</label>
                <select
                  className="form-select form-select-sm"
                  value={gladiatorOfGameName}
                  onChange={(e) => setGladiatorOfGameName(e.target.value)}
                  disabled={isGeneratingRecap}
                >
                  <option value="">— Select player —</option>
                  {(endGameModal.lineup || []).filter((p) => p?.name).map((p) => (
                    <option key={p.name} value={p.name}>{p.name}</option>
                  ))}
                </select>
              </div>
              {gladiatorOfGameName && (
                <div className="mb-3">
                  <label className="form-label small text-muted-brand mb-1">Why they were Gladiator of the Game</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    placeholder="e.g. Two goals, great backchecking, huge saves"
                    value={gladiatorOfGameReason}
                    onChange={(e) => setGladiatorOfGameReason(e.target.value)}
                    disabled={isGeneratingRecap}
                  />
                </div>
              )}

              <div className="d-flex gap-2 mt-3">
                <button type="button" className="btn btn-teal flex-fill" onClick={handleGenerateRecap} disabled={isGeneratingRecap}>
                  {isGeneratingRecap ? 'Generating…' : 'Generate recap'}
                </button>
                <button type="button" className="btn btn-neutral flex-fill" onClick={handleEndGameModalDone} disabled={isGeneratingRecap}>
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container py-4">
        {/* Header + status */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <h1 className="h3 m-0">
            Hockey Stat Tracker
            {teamAgeLabel ? (
              <span className="text-muted-brand ms-2 fw-normal fs-5">· {teamAgeLabel}</span>
            ) : null}
          </h1>
          <div className="d-flex align-items-center gap-2">
            <button 
              className="btn btn-sm btn-neutral"
              onClick={toggleAllSections}
              title="Toggle all sections"
            >
              {Object.values(sectionVisibility).every(v => v) ? 'Hide All' : 'Show All'}
            </button>
            <span className={`badge ${isOffline ? 'bg-danger' : 'badge-gold'}`}>{isOffline ? 'Offline' : 'Online'}</span>
          </div>
        </div>
       

        {/* Game Management */}
        <div className="card card-glad mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h2 className="h5 section-title mb-0">Game Management</h2>
              <button
                className="btn btn-sm btn-neutral"
                onClick={() => setIsGameMgmtOpen(o => !o)}
              >
                {isGameMgmtOpen ? "Minimize" : "Expand"}
              </button>
            </div>

            {isGameMgmtOpen && (
              <>
                {/* Team Roster — usable before any game exists (statsDev / first setup) */}
                <div className="alert alert-info mb-3" style={{ background: 'rgba(0, 109, 117, 0.15)', border: '1px solid rgba(0, 109, 117, 0.3)', color: '#e6e8ec', padding: '0.75rem' }}>
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-0">
                    <div>
                      <strong style={{ color: 'var(--brand-gold)' }}>Team Roster:</strong>{' '}
                      {teamRoster.length > 0 ? (
                        <span>{teamRoster.length} players saved</span>
                      ) : (
                        <span className="text-warning">No team roster saved yet</span>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {isMasterRosterOpen && (
                        <button
                          type="button"
                          className="btn btn-sm btn-teal"
                          onClick={handleUpdateTeamRoster}
                          disabled={lineup.length === 0}
                          title={
                            lineup.length === 0
                              ? 'Optional: copy players from an open game into the master roster'
                              : 'Save current game lineup as team roster'
                          }
                        >
                          Copy from current game
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-neutral"
                        onClick={() => setIsMasterRosterOpen((o) => !o)}
                        aria-expanded={isMasterRosterOpen}
                      >
                        {isMasterRosterOpen ? 'Minimize' : 'Expand'}
                      </button>
                    </div>
                  </div>
                  {isMasterRosterOpen && (
                    <>
                      <p className="small text-muted-brand mb-2 mt-2">
                        Set the master roster <strong>before</strong> creating a game (upload CSV or add players). New games start with this list. Separate for 12U and 14U.
                      </p>
                      <div className="small text-muted-brand mb-1">
                        CSV header: <strong>name, number, position</strong> (position G = goalie)
                      </div>
                      <input
                        type="file"
                        accept=".csv"
                        className="form-control form-control-sm bg-dark text-light border-0 mb-2"
                        onChange={handleMasterRosterUpload}
                      />
                      <div className="row g-2 align-items-end mb-2">
                        <div className="col-12 col-md-4">
                          <label className="small text-muted-brand mb-1">Player name</label>
                          <input
                            className="form-control form-control-sm bg-dark text-light border-0"
                            placeholder="First and last name"
                            value={inputName}
                            onChange={(e) => setInputName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleAddToMasterRoster();
                            }}
                          />
                        </div>
                        <div className="col-6 col-md-2">
                          <label className="small text-muted-brand mb-1">#</label>
                          <select
                            className="form-select form-select-sm bg-dark text-light border-0"
                            value={inputNumber}
                            onChange={(e) => setInputNumber(e.target.value)}
                          >
                            <option value="">—</option>
                            {Array.from({ length: 99 }, (_, i) => i + 1).map((num) => (
                              <option key={num} value={num}>
                                {num}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-6 col-md-3">
                          <label className="small text-muted-brand mb-1">Pos</label>
                          <select
                            className="form-select form-select-sm bg-dark text-light border-0"
                            value={inputPosition}
                            onChange={(e) => setInputPosition(e.target.value)}
                          >
                            <option value="">Select</option>
                            <option value="F">Forward</option>
                            <option value="D">Defense</option>
                            <option value="G">Goalie</option>
                          </select>
                        </div>
                        <div className="col-12 col-md-3 d-grid">
                          <button type="button" className="btn btn-sm btn-teal" onClick={handleAddToMasterRoster}>
                            Add to roster
                          </button>
                        </div>
                      </div>
                      {rosterMessage && <div className="small text-success mb-2">{rosterMessage}</div>}
                      {teamRoster.length > 0 && (
                        <ul className="list-group mb-0 master-roster-list" style={{ maxHeight: 220, overflowY: 'auto' }}>
                          {teamRoster.map((p, idx) => (
                            <li
                              key={`master-${p.name}-${idx}`}
                              className="list-group-item d-flex align-items-center gap-2 py-1 px-2"
                              style={{
                                background: 'var(--brand-card)',
                                color: '#e6e8ec',
                                borderColor: 'var(--brand-border)',
                                fontSize: '0.85rem',
                              }}
                            >
                              <span className="flex-grow-1 text-truncate" style={{ minWidth: 0 }}>
                                <strong>{p.name}</strong>
                                {p.number ? ` #${p.number}` : ''}
                                {p.position ? ` · ${p.position}` : ''}
                                {p.isGoalie ? ' · G' : ''}
                              </span>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger flex-shrink-0"
                                style={{ width: 'auto', minWidth: 'auto', padding: '0.15rem 0.45rem', fontSize: '0.75rem' }}
                                onClick={() => handleRemoveFromMasterRoster(p.name)}
                                aria-label={`Remove ${p.name} from roster`}
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
                
                <div className="row g-2 mt-2">
  <div className="col-12 col-md-3">
    <label className="small text-light mb-1">Opponent *</label>
    <input
      className="form-control form-control-sm bg-dark text-light border-0"
      value={newGameDetails.opponent}
      onChange={(e) =>
        setNewGameDetails({ ...newGameDetails, opponent: e.target.value })
      }
      placeholder="Required"
    />
  </div>
  <div className="col-12 col-md-3">
    <label className="small text-light mb-1">Date</label>
    <input
      type="date"
      className="form-control form-control-sm bg-dark text-light border-0"
      value={newGameDetails.date}
      onChange={(e) =>
        setNewGameDetails({ ...newGameDetails, date: e.target.value })
      }
    />
  </div>
  <div className="col-12 col-md-3">
    <label className="small text-light mb-1">Location *</label>
    <input
      className="form-control form-control-sm bg-dark text-light border-0"
      value={newGameDetails.location}
      onChange={(e) =>
        setNewGameDetails({ ...newGameDetails, location: e.target.value })
      }
      placeholder="Required"
    />
  </div>
  <div className="col-12 col-md-3 d-grid">
    <button className="btn btn-sm btn-teal" onClick={handleCreateNewGame}>
      Create New
    </button>
  </div>
</div>


              {/* Game History */}
<div className="mt-3">
  <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
    <div className="text-muted-brand">Game History</div>
    <div className="d-flex gap-2 align-items-center">
      <button
        className="btn btn-teal btn-sm"
        onClick={handleBackfillGameHistory}
        disabled={isBackfillingHistory || archivedGames.length === 0}
        title={archivedGames.length === 0 ? 'No archived games to sync' : 'Sync all archived games into the master record for analytics'}
      >
        {isBackfillingHistory ? 'Syncing…' : 'Sync to master record'}
      </button>
      <button 
        className="btn btn-neutral btn-sm"
        onClick={() => setShowArchivedGames(v => !v)}
      >
        {showArchivedGames ? 'Show Active' : `Show Archived (${archivedGames.length})`}
      </button>
    </div>
  </div>
  <p className="small text-muted-brand mb-2">Ended games are saved to the master record automatically. &quot;In master record&quot; means already synced—re-sync only if you edited an archived game.</p>
  <div className="list-group">
    {displayedGames.length === 0 && (
      <div className="list-group-item text-center" style={{ background: 'var(--brand-card)', color: '#e6e8ec', borderColor: 'var(--brand-border)' }}>
        {showArchivedGames ? 'No archived games' : 'No active games'}
      </div>
    )}
    {displayedGames.map((g) => (
      <div
        key={g.id}
        className={`list-group-item d-flex justify-content-between align-items-center ${
          selectedGameId === g.id ? 'active' : ''
        }`}
        style={{
          background: selectedGameId === g.id ? 'var(--brand-teal)' : 'var(--brand-card)',
          color: '#e6e8ec',
          borderColor: 'var(--brand-border)',
        }}
      >
        <button
          className="btn btn-link text-light p-0 flex-grow-1 text-start"
          onClick={() => {
            if (g.archived && gamesColRef) {
              updateDoc(doc(gamesColRef, g.id), { live: false });
              if (validTeamId && typeof localStorage !== 'undefined') localStorage.setItem(`gcg-live-game:${teamId}`, '');
            } else if (validTeamId && typeof localStorage !== 'undefined') {
              localStorage.setItem(`gcg-live-game:${teamId}`, g.id);
            }
            setSelectedGameId(g.id);
          }}
        >
          <strong>{g?.details?.opponent || 'Game'}</strong>
          <span className="ms-2 small text-muted-brand">{g?.details?.date || ''}</span>
          {g.archived && <span className="ms-2 badge bg-secondary">Archived</span>}
          {g.syncedToMasterAt && (
            <span className="ms-2 badge bg-success" title={`Synced to master record${g.syncedToMasterAt ? ` ${new Date(g.syncedToMasterAt).toLocaleDateString()}` : ''}`}>
              In master record
            </span>
          )}
        </button>
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-warning"
            onClick={() => handleArchiveGame(g.id, !g.archived)}
          >
            {g.archived ? 'Unarchive' : 'Archive'}
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={() => {
              if (window.confirm(`Are you sure you want to permanently delete the game vs. ${g?.details?.opponent || 'Opponent'}?`)) {
                handleDeleteGame(g.id);
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>
    ))}
  </div>
</div>
    

              </>
            )}
          </div>
        </div>

        {currentGame && !currentGame.archived && (
          <>
            {/* Game header */}
            <div className="card card-glad mb-3">
              <div className="card-body" style={{ padding: '0.75rem' }}>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div>
                    <div className="section-title h5 mb-1">{currentGame?.details?.opponent ||"Opponent TBD"}</div>
                    <div className="small text-muted-brand">
                      <strong>Date:</strong> {currentGame?.details?.date ||"-"}
                      <span className="ms-3"><strong>Location:</strong> {currentGame?.details?.location || "-"}</span>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-neutral btn-sm" onClick={() => handlePeriodChange(-1)}>−</button>
                    <div className="text-center">
                      <div className="small text-muted-brand">Period</div>
                      <div className="fs-4 fw-bold">{gameData.currentPeriod <= 3 ? gameData.currentPeriod : 'OT'}</div>
                    </div>
                    <button className="btn btn-neutral btn-sm" onClick={() => handlePeriodChange(1)}>+</button>
                    <button
                      className="btn btn-warning btn-sm ms-2"
                      onClick={handleReArchiveGame}
                      title="Re-archive and update master record (e.g. after editing)"
                    >
                      Re-archive game
                    </button>
                    <button 
                      className="btn btn-danger btn-sm ms-2" 
                      onClick={handleEndGame}
                      style={{ fontWeight: '700' }}
                      title="Archive game and lock stats"
                    >
                      End Game
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Scoreboard & quick actions */}
            <div className="row g-3 mb-4">
              <div className="col-12 col-lg-6">
                <div className="card card-glad h-100">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="section-title h5 mb-0">Scoreboard</div>
                      <button 
                        className="btn btn-neutral btn-sm"
                        onClick={() => toggleSection('scoreboard')}
                      >
                        {sectionVisibility.scoreboard ? 'Hide' : 'Show'}
                      </button>
                    </div>

                    {sectionVisibility.scoreboard && (
                      <>
                        {/* Period indicator (3 dots) */}
                        <div className="text-center mb-2">
                          <div className="small text-muted-brand">Period</div>
                          <div className="d-flex justify-content-center gap-2 mt-1 align-items-center">
                            {[1,2,3].map((n)=> (
                              <span key={n} aria-label={`Period ${n}`} style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: gameData.currentPeriod >= n ? 'red' : 'transparent', border: '1px solid var(--brand-border)'}} />
                            ))}
                            {gameData.currentPeriod >= 4 && (
                              <span className="badge bg-warning ms-2" style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem', fontWeight: '700' }}>
                                OT
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="row text-center">
                          <div className="col-6">
                            <div className="small text-muted-brand">Gladiators</div>
                            <div className="kpi" style={{ color: 'var(--brand-gold)' }}>{gameData.score.us}</div>
                            <div className="small text-muted-brand mt-1 d-sm-none">Shots: {gameData.shots.us}</div>
                          </div>
                          <div className="col-6">
                            <div className="small text-muted-brand">{currentGame?.details?.opponent || "Opponent"}</div>
                            <div className="kpi" style={{ color: '#e6e8ec' }}>{gameData.score.them}</div>
                            <div className="small text-muted-brand mt-1 d-sm-none">Shots: {gameData.shots.them}</div>
                          </div>
                        </div>

                        <div className="row text-center mt-3 d-none d-sm-flex">
                          <div className="col-6">
                            <div className="small text-muted-brand mb-1">Shots For</div>
                            <div className="fs-3 fw-bold">{gameData.shots.us}</div>
                            <div className="d-flex justify-content-center gap-2 mt-2">
                              <button className="btn btn-danger btn-sm" onClick={handleRemoveShotUs}>−</button>
                              <button className="btn btn-teal btn-sm" onClick={handleShotUs}>+</button>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="small text-muted-brand mb-1">Shots Against</div>
                            <div className="fs-3 fw-bold">{gameData.shots.them}</div>
                            <div className="d-flex justify-content-center gap-2 mt-2">
                              <button className="btn btn-danger btn-sm" onClick={handleRemoveShotThem}>−</button>
                              <button className="btn btn-teal btn-sm" onClick={handleShotThem}>+</button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Goalies — set goalies in roster; activate one to attribute saves/goals allowed */}
              <div className="col-12 col-lg-6">
                <div className="card card-glad h-100">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="section-title h5 mb-0">Goalies</div>
                      <button 
                        className="btn btn-neutral btn-sm"
                        onClick={() => toggleSection('goalie')}
                      >
                        {sectionVisibility.goalie ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    {sectionVisibility.goalie && (
                      <>
                        {goalies.length === 0 ? (
                          <div className="text-muted-brand">Set players as goalies in the roster below (click &quot;Set G&quot; on a skater).</div>
                        ) : (
                          <>
                            <div className="small text-muted-brand mb-2">Activate the goalie in net. <strong>+ Shots Against</strong> and <strong>Log Save</strong> both add a save for them. <strong>Opp Goal</strong> adds a shot against and a goal allowed.</div>
                            {goalies.map((g) => {
                              const saves = g.saves || 0;
                              const ga = g.goalsAllowed || 0;
                              const shotsAgainstG = saves + ga;
                              const savePctG = shotsAgainstG > 0 ? (saves / shotsAgainstG) * 100 : 0;
                              const isActive = activeGoalieName === g.name;
                              return (
                                <div key={g.name} className="d-flex justify-content-between align-items-center flex-wrap gap-2 py-2 border-bottom border-secondary" style={{ borderColor: 'var(--brand-border)' }}>
                                  <div className="d-flex align-items-center gap-2 flex-grow-1">
                                    <div className="fw-bold">{g.name}</div>
                                    <span className="chip-goalie">Goalie</span>
                                    {isActive && <span className="badge bg-success">Active</span>}
                                  </div>
                                  <div className="d-flex align-items-center gap-3">
                                    <div className="text-end small">
                                      <span className="text-muted-brand">Saves</span>
                                      <div className="fw-bold">{saves}</div>
                                    </div>
                                    <div className="text-end small">
                                      <span className="text-muted-brand">GA</span>
                                      <div className="fw-bold">{ga}</div>
                                    </div>
                                    <div className="text-end small">
                                      <span className="text-muted-brand">SV%</span>
                                      <div className="fw-bold">{savePctG.toFixed(1)}%</div>
                                    </div>
                                    <button
                                      type="button"
                                      className={`btn btn-sm ${isActive ? 'btn-warning' : 'btn-teal'}`}
                                      onClick={() => handleSetActiveGoalie(g.name)}
                                    >
                                      {isActive ? 'Deactivate' : 'Activate'}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                            <div className="d-flex gap-2 mt-3 d-none d-sm-flex">
                              <button className="btn btn-sm btn-teal flex-fill" onClick={handleAddSave} disabled={!activeGoalieName} title={activeGoalieName ? `Log save for ${activeGoalieName}` : 'Activate a goalie first'}>
                                Log Save
                              </button>
                              <button className="btn btn-sm btn-danger flex-fill" onClick={handleRemoveSave} disabled={!activeGoalieName || !activeGoalie || (activeGoalie.saves || 0) === 0} title={activeGoalieName ? `Remove save from ${activeGoalieName}` : 'Activate a goalie first'}>
                                Remove Save
                              </button>
                            </div>
                            {!activeGoalieName && goalies.length > 0 && (
                              <div className="small text-warning mt-2">Select &quot;Activate&quot; next to the goalie in net to log saves.</div>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Game Log - placed high for stat keeper; sticky on mobile/tablet */}
            <div className="card card-glad mb-5 game-log-sticky-mobile">
  <div className="card-body">
    <div className="d-flex justify-content-between align-items-center mb-3">
      <div className="section-title h5 mb-0">Game Log</div>
      <button 
        className="btn btn-neutral btn-sm"
        onClick={() => toggleSection('gameLog')}
      >
        {sectionVisibility.gameLog ? 'Hide' : 'Show'}
      </button>
    </div>
    {sectionVisibility.gameLog && (
      <ul className="list-group">
      {gameData.gameLog.map((e, i) => (
        <li
          key={`${e.type}-${i}`}
          className="list-group-item"
          style={{
            background: 'var(--brand-card)',
            color: '#e6e8ec',
            borderColor: 'var(--brand-border)',
            padding: editingLogIndex === i ? '0.75rem 1rem' : '0.5rem 0.75rem'
          }}
        >
          {editingLogIndex === i && editDraft ? (
            <>
              <div className="small text-muted-brand mb-2">
                Period {e.period || '-'} at {e.time} <span className="ms-2">(unchanged)</span>
              </div>
              {editDraft.type === 'goal_us' && (
                <div className="row g-2 align-items-end">
                  <div className="col-12 col-md-3">
                    <label className="small text-muted-brand mb-1">Scorer</label>
                    <select
                      className="form-select form-select-sm bg-dark text-light border-0"
                      value={editDraft.scorer}
                      onChange={(ev) => setEditDraft((d) => ({ ...d, scorer: ev.target.value }))}
                    >
                      <option value="">Select</option>
                      {lineup.filter((p) => !p.isGoalie).map((p) => (
                        <option key={p.name} value={p.name}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="small text-muted-brand mb-1">Assist 1</label>
                    <select
                      className="form-select form-select-sm bg-dark text-light border-0"
                      value={editDraft.assist1}
                      onChange={(ev) => setEditDraft((d) => ({ ...d, assist1: ev.target.value }))}
                    >
                      <option value="">—</option>
                      {lineup.filter((p) => !p.isGoalie).map((p) => (
                        <option key={p.name} value={p.name}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="small text-muted-brand mb-1">Assist 2</label>
                    <select
                      className="form-select form-select-sm bg-dark text-light border-0"
                      value={editDraft.assist2}
                      onChange={(ev) => setEditDraft((d) => ({ ...d, assist2: ev.target.value }))}
                    >
                      <option value="">—</option>
                      {lineup.filter((p) => !p.isGoalie).map((p) => (
                        <option key={p.name} value={p.name}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="small text-muted-brand mb-1">Assist 3</label>
                    <select
                      className="form-select form-select-sm bg-dark text-light border-0"
                      value={editDraft.assist3}
                      onChange={(ev) => setEditDraft((d) => ({ ...d, assist3: ev.target.value }))}
                    >
                      <option value="">—</option>
                      {lineup.filter((p) => !p.isGoalie).map((p) => (
                        <option key={p.name} value={p.name}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 mt-2 d-flex gap-2 flex-wrap">
                    <button type="button" className="btn btn-sm btn-teal" onClick={handleSaveEditLogEntry}>Save</button>
                    <button type="button" className="btn btn-sm btn-neutral" onClick={handleCancelEdit}>Cancel</button>
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => handleRemoveLogEntry(editingLogIndex)}>Remove entry</button>
                  </div>
                </div>
              )}
              {editDraft.type === 'penalty' && (
                <div className="row g-2 align-items-end">
                  <div className="col-12 col-md-3">
                    <label className="small text-muted-brand mb-1">Player</label>
                    <select
                      className="form-select form-select-sm bg-dark text-light border-0"
                      value={editDraft.player}
                      onChange={(ev) => setEditDraft((d) => ({ ...d, player: ev.target.value }))}
                    >
                      <option value="">Select</option>
                      {lineup.map((p) => (
                        <option key={p.name} value={p.name}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="small text-muted-brand mb-1">Type</label>
                    <select
                      className="form-select form-select-sm bg-dark text-light border-0"
                      value={editDraft.penaltyType}
                      onChange={(ev) => setEditDraft((d) => ({ ...d, penaltyType: ev.target.value }))}
                    >
                      <option value="">Select</option>
                      {penaltyTypes.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="small text-muted-brand mb-1">Duration</label>
                    <select
                      className="form-select form-select-sm bg-dark text-light border-0"
                      value={editDraft.duration}
                      onChange={(ev) => setEditDraft((d) => ({ ...d, duration: ev.target.value }))}
                    >
                      {penaltyOptions.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-3 d-flex gap-2 flex-wrap">
                    <button type="button" className="btn btn-sm btn-teal" onClick={handleSaveEditLogEntry}>Save</button>
                    <button type="button" className="btn btn-sm btn-neutral" onClick={handleCancelEdit}>Cancel</button>
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => handleRemoveLogEntry(editingLogIndex)}>Remove entry</button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="d-flex justify-content-between align-items-start gap-2">
              <div className="flex-grow-1" style={{ minWidth: 0, lineHeight: 1.35 }}>
                {e.type === 'goal_us' && (
                  <strong style={{ color: 'var(--brand-gold)' }}>[Our Goal]</strong>
                )}
                {e.type === 'goal_them' && (
                  <strong className="text-danger">[Opponent Goal]</strong>
                )}
                {e.type === 'penalty' && (
                  <strong className="text-warning">[Penalty]</strong>
                )}

                {e.scorer && ` ${e.scorer} scores!`}
                {e.assist1 && ` (Assist: ${e.assist1})`}
                {e.assist2 && ` (2nd Assist: ${e.assist2})`}
                {e.assist3 && ` (3rd Assist: ${e.assist3})`}

                {e.type === 'penalty' &&
                  ` ${e.player} — ${e.penaltyType} (${e.duration})`}

                <span className="ms-2 small text-muted-brand">
                  Period {e.period || '-'} · {e.time}
                </span>
              </div>
              <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                {(e.type === 'goal_us' || e.type === 'penalty') && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-light"
                    style={{ fontSize: '0.7rem', padding: '0.15rem 0.45rem', lineHeight: 1.2 }}
                    onClick={() => handleStartEditLogEntry(i)}
                    title="Edit this entry"
                  >
                    Edit
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  style={{ fontSize: '0.7rem', padding: '0.15rem 0.45rem', lineHeight: 1.2 }}
                  onClick={() => handleRemoveLogEntry(i)}
                  title="Remove this entry from the game log"
                >
                  Remove
                </button>
              </div>
            </div>
          )}
        </li>
      ))}
    </ul>
    )}
  </div>
</div>

            {/* Period Breakdown */}
            <div className="card card-glad mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="section-title h5 mb-0">Period Breakdown</div>
                  <button 
                    className="btn btn-neutral btn-sm"
                    onClick={() => toggleSection('periodBreakdown')}
                  >
                    {sectionVisibility.periodBreakdown ? 'Hide' : 'Show'}
                  </button>
                </div>
                {sectionVisibility.periodBreakdown && (
                  <>
                    <div className="small text-muted-brand mb-2">Shots For (SOF) vs Shots Against (SOA) by Period</div>
                    <div className="table-responsive">
                      <table className="table table-borderless align-middle" style={{ fontSize: '0.9rem', marginBottom: 0, background: 'transparent' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid var(--brand-teal)' }}>
                            <th style={{ color: 'var(--brand-gold)', fontWeight: '700', textTransform: 'uppercase', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none', fontSize: '0.9rem' }}>Period</th>
                            <th style={{ color: 'var(--brand-gold)', fontWeight: '700', textTransform: 'uppercase', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none', fontSize: '0.9rem' }}>SOF</th>
                            <th style={{ color: 'var(--brand-gold)', fontWeight: '700', textTransform: 'uppercase', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none', fontSize: '0.9rem' }}>SOA</th>
                            <th style={{ color: 'var(--brand-gold)', fontWeight: '700', textTransform: 'uppercase', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none', fontSize: '0.9rem' }}>Diff</th>
                          </tr>
                        </thead>
                        <tbody style={{ background: 'transparent' }}>
                          {[1, 2, 3, 4].map((period) => {
                            const sof = (gameData.shotsByPeriod?.us && gameData.shotsByPeriod.us[period]) || 0;
                            const soa = (gameData.shotsByPeriod?.them && gameData.shotsByPeriod.them[period]) || 0;
                            const diff = sof - soa;
                            const periodLabel = period <= 3 ? `Period ${period}` : 'OT';
                            
                            return (
                              <tr key={period} style={{ background: 'transparent', border: 'none' }}>
                                <td style={{ fontWeight: '600', color: '#ffffff', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none', fontSize: '0.95rem' }}>{periodLabel}</td>
                                <td style={{ fontWeight: '700', color: 'var(--brand-teal)', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none', fontSize: '1.1rem' }}>{sof}</td>
                                <td style={{ fontWeight: '700', color: '#ffffff', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none', fontSize: '1.1rem' }}>{soa}</td>
                                <td style={{ padding: '0.75rem 0.5rem', background: 'transparent', border: 'none' }}>
                                  <span className={`badge ${diff > 0 ? 'bg-success' : diff < 0 ? 'bg-danger' : 'bg-secondary'}`} style={{ fontSize: '0.85rem', padding: '0.4rem 0.7rem', fontWeight: '700' }}>
                                    {diff > 0 ? '+' : ''}{diff}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                          <tr style={{ borderTop: '2px solid var(--brand-teal)', background: 'transparent', border: 'none' }}>
                            <td style={{ fontWeight: '700', fontSize: '1.05rem', color: 'var(--brand-gold)', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none' }}>TOTAL</td>
                            <td style={{ fontWeight: '700', fontSize: '1.2rem', color: 'var(--brand-teal)', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none' }}>{gameData.shots.us}</td>
                            <td style={{ fontWeight: '700', fontSize: '1.2rem', color: '#ffffff', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none' }}>{gameData.shots.them}</td>
                            <td style={{ padding: '0.75rem 0.5rem', background: 'transparent', border: 'none' }}>
                              <span className={`badge ${gameData.shots.us - gameData.shots.them > 0 ? 'bg-success' : gameData.shots.us - gameData.shots.them < 0 ? 'bg-danger' : 'bg-secondary'}`} style={{ fontSize: '0.9rem', padding: '0.4rem 0.7rem', fontWeight: '700' }}>
                                {gameData.shots.us - gameData.shots.them > 0 ? '+' : ''}{gameData.shots.us - gameData.shots.them}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Mobile Quick Actions (sticky inside container) */}
            <div className="d-sm-none" style={{ position: 'sticky', bottom: 0, zIndex: 20, background: 'rgba(7,13,17,0.95)', borderTop: '1px solid var(--brand-border)', padding: '0.5rem', marginLeft: -12, marginRight: -12, backdropFilter: 'blur(6px)' }}>
              <div className="d-flex justify-content-between gap-2">
                <button className="btn btn-sm btn-danger flex-fill" onClick={handleRemoveShotUs}>− Shot</button>
                <button className="btn btn-sm btn-teal flex-fill" onClick={handleShotUs}>+ Shot</button>
                <button className="btn btn-sm btn-neutral flex-fill" onClick={() => handlePeriodChange(1)}>Period</button>
              </div>
              <div className="d-flex justify-content-between gap-2 mt-2">
                <button className="btn btn-sm btn-danger flex-fill" onClick={handleRemoveSave}>− Save</button>
                <button className="btn btn-sm btn-teal flex-fill" onClick={handleAddSave}>+ Save</button>
                <button className="btn btn-sm btn-neutral flex-fill" onClick={handleAddOpponentGoal}>Opp Goal</button>
              </div>
            </div>

            {/* Quick: Log a Goal */}
            <div className="card card-glad mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="section-title h5 mb-0">Log a Goal</div>
                  <button 
                    className="btn btn-neutral btn-sm"
                    onClick={() => toggleSection('logGoal')}
                  >
                    {sectionVisibility.logGoal ? 'Hide' : 'Show'}
                  </button>
                </div>
                {sectionVisibility.logGoal && (
                  <>
                    <div className="row g-2">
                      <div className="col-12 col-md-3">
                        <label className="small text-muted-brand mb-1">Scorer *</label>
                        <select className="form-select form-select-sm bg-dark text-light border-0" value={scorerName} onChange={(e)=>setScorerName(e.target.value)}>
                          <option value="">Select Scorer</option>
                          {lineup.filter(p=>!p.isGoalie).map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                        </select>
                      </div>
                      <div className="col-12 col-md-3">
                        <label className="small text-muted-brand mb-1">Primary Assist</label>
                        <select className="form-select form-select-sm bg-dark text-light border-0" value={assist1Name} onChange={(e)=>setAssist1Name(e.target.value)}>
                          <option value="">Optional</option>
                          {lineup.filter(p=>!p.isGoalie && p.name!==scorerName).map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                        </select>
                      </div>
                      <div className="col-12 col-md-3">
                        <label className="small text-muted-brand mb-1">Secondary Assist</label>
                        <select className="form-select form-select-sm bg-dark text-light border-0" value={assist2Name} onChange={(e)=>setAssist2Name(e.target.value)}>
                          <option value="">Optional</option>
                          {lineup.filter(p=>!p.isGoalie && ![scorerName,assist1Name].includes(p.name)).map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                        </select>
                      </div>
                      <div className="col-12 col-md-3">
                        <label className="small text-muted-brand mb-1">Third Assist</label>
                        <select className="form-select form-select-sm bg-dark text-light border-0" value={assist3Name} onChange={(e)=>setAssist3Name(e.target.value)}>
                          <option value="">Optional</option>
                          {lineup.filter(p=>!p.isGoalie && ![scorerName,assist1Name,assist2Name].includes(p.name)).map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="row g-2 mt-3">
                      <div className="col-6 col-md-3">
                        <button className="btn btn-sm btn-teal w-100" onClick={handleAddOurGoal}>Log Our Goal</button>
                      </div>
                      <div className="col-6 col-md-3">
                        <button className="btn btn-sm btn-danger w-100" onClick={handleRemoveOurGoal}>Remove Our Goal</button>
                      </div>
                      <div className="col-6 col-md-3">
                        <button className="btn btn-sm btn-neutral w-100" onClick={handleAddOpponentGoal}>Log Opp Goal</button>
                      </div>
                      <div className="col-6 col-md-3">
                        <button className="btn btn-sm btn-warning w-100" onClick={handleRemoveOpponentGoal}>Remove Opp Goal</button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* NEW: Plus/Minus Tracker - SEPARATE SECTION */}
            <div className="card card-glad mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="section-title h5 mb-0">Plus/Minus Tracker</div>
                  <button 
                    className="btn btn-neutral btn-sm" 
                    onClick={() => toggleSection('plusMinus')}
                  >
                    {sectionVisibility.plusMinus ? 'Hide' : 'Show'}
                  </button>
                </div>

                {sectionVisibility.plusMinus && (
                  <>
                    {/* Sort Dropdown - same as roster */}
                    <div className="mb-3">
                      <label className="small text-muted-brand mb-1">Sort Players By</label>
                      <select 
                        className="form-select form-select-sm bg-dark text-light border-0"
                        value={rosterSortBy}
                        onChange={(e) => setRosterSortBy(e.target.value)}
                      >
                        <option value="default">Order Added</option>
                        <option value="firstName">First Name (A-Z)</option>
                        <option value="lastName">Last Name (A-Z)</option>
                        <option value="number">Jersey Number</option>
                        <option value="plusMinus">Plus/Minus (High to Low)</option>
                      </select>
                    </div>

                    <div className="small text-muted-brand mb-3">Track +/- for each player during the game</div>
                    <div className="row g-2">
                      {sortedLineup.filter(p => !p.isGoalie).map((p, idx) => (
                        <div key={`pm-${p.name}-${idx}`} className="col-12 col-md-6">
                          <div className="d-flex align-items-center justify-content-between p-2 rounded" 
                               style={{ background: 'var(--brand-card)', border: '1px solid var(--brand-border)' }}>
                            <div className="d-flex align-items-center gap-2 flex-grow-1">
                              <span className="fw-semibold" style={{ minWidth: '100px' }}>{p.name}</span>
                              {p.number && <span className="badge bg-info">#{p.number}</span>}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <span className={`badge ${p.plusMinus > 0 ? 'bg-success' : p.plusMinus < 0 ? 'bg-danger' : 'bg-secondary'}`}
                                    style={{ minWidth: '50px', fontSize: '0.9rem' }}>
                                {p.plusMinus > 0 ? '+' : ''}{p.plusMinus || 0}
                              </span>
                              {/* Large buttons on mobile (d-sm-none), small on desktop (d-none d-sm-inline-block) */}
                              <button 
                                className="btn btn-success d-sm-none" 
                                style={{ padding: '0.5rem 0.85rem', fontSize: '1.1rem', minWidth: '50px', fontWeight: '700' }}
                                onClick={()=>handlePlusMinusChange(p.name, 1)}
                              >
                                +
                              </button>
                              <button 
                                className="btn btn-sm btn-success d-none d-sm-inline-block" 
                                onClick={()=>handlePlusMinusChange(p.name, 1)}
                              >
                                +
                              </button>
                              <button 
                                className="btn btn-danger d-sm-none" 
                                style={{ padding: '0.5rem 0.85rem', fontSize: '1.1rem', minWidth: '50px', fontWeight: '700' }}
                                onClick={()=>handlePlusMinusChange(p.name, -1)}
                              >
                                −
                              </button>
                              <button 
                                className="btn btn-sm btn-danger d-none d-sm-inline-block" 
                                onClick={()=>handlePlusMinusChange(p.name, -1)}
                              >
                                −
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Penalties */}
            <div className="card card-glad mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="section-title h5 mb-0">Penalty Tracker</div>
                  <button 
                    className="btn btn-neutral btn-sm"
                    onClick={() => toggleSection('penalties')}
                  >
                    {sectionVisibility.penalties ? 'Hide' : 'Show'}
                  </button>
                </div>
                {sectionVisibility.penalties && (
                  <>
                    <div className="row g-2">
                  <div className="col-12 col-md-3">
                    <label className="small text-muted-brand mb-1">Player</label>
                    <select className="form-select form-select-sm bg-dark text-light border-0" value={penaltyPlayer} onChange={(e)=>setPenaltyPlayer(e.target.value)}>
                      <option value="">Select</option>
                      {lineup.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                    </select>
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="small text-muted-brand mb-1">Type</label>
                    <select className="form-select form-select-sm bg-dark text-light border-0" value={penaltyType} onChange={(e)=>setPenaltyType(e.target.value)}>
                      <option value="">Select</option>
                      {penaltyTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="small text-muted-brand mb-1">Duration</label>
                    {!useCustomPenalty ? (
                      <div className="d-flex gap-2">
                        <select className="form-select form-select-sm bg-dark text-light border-0" value={penaltyDuration} onChange={(e)=>setPenaltyDuration(e.target.value)}>
                          {penaltyOptions.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                        <button className="btn btn-sm btn-neutral" onClick={()=>setUseCustomPenalty(true)}>Custom</button>
                      </div>
                    ) : (
                      <div className="d-flex align-items-center gap-2">
                        <input type="number" className="form-control form-control-sm bg-dark text-light border-0" placeholder="Mins" min="0" value={customPenaltyMins} onChange={(e)=>setCustomPenaltyMins(e.target.value)} />
                        <span>:</span>
                        <input type="number" className="form-control form-control-sm bg-dark text-light border-0" placeholder="Secs" min="0" max="59" value={customPenaltySecs} onChange={(e)=>setCustomPenaltySecs(e.target.value)} />
                        <button className="btn btn-sm btn-neutral" onClick={()=>{ const m = Math.max(0, parseInt(customPenaltyMins||'0',10)); const s = Math.max(0, Math.min(59, parseInt(customPenaltySecs||'0',10))); setPenaltyDuration(`${m}:${String(s).padStart(2,'0')}`); setUseCustomPenalty(false); }}>Use</button>
                      </div>
                    )}
                  </div>
                  <div className="col-12 col-md-3 d-grid">
                    <button className="btn btn-sm btn-danger" onClick={handleAddPenalty}>Log Penalty</button>
                  </div>
                </div>

                <div className="mt-3">
                  <div className="text-muted-brand mb-2">Current Penalties</div>
                  <ul className="list-group">
                    {gameData.penalties.map((pen,i)=> (
                      <li key={`${pen.player}-${i}`} className="list-group-item" style={{ background:'var(--brand-card)', color:'#e6e8ec', borderColor:'var(--brand-border)', padding: '0.5rem 0.75rem' }}>
                        <strong>{pen.player}</strong> — {pen.type} ({pen.duration})
                        <span className="ms-2 small text-muted-brand">
  Period {pen.period || '-'} at {pen.time}
</span>

                      </li>
                    ))}
                  </ul>
                </div>
                  </>
                )}
              </div>
            </div>

            {/* Player Stats + Find your skater */}
<div className="card card-glad mb-4">
  <div className="card-body">
    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
      <div className="section-title h5 mb-0">Game Day Stats</div>
      <button
        className="btn btn-neutral btn-sm"
        onClick={() => {
          toggleSection('stats');
          setIsStatsVisible(v => !v);
        }}
      >
        {sectionVisibility.stats ? "Hide" : "Show"}
      </button>
    </div>

    {sectionVisibility.stats && (
      <>
        <div className="mt-3 mb-3">
          <label className="small text-muted-brand d-block mb-1" style={{ fontWeight: '600' }}>Find your skater</label>
          <p className="small text-muted-brand mb-2">Select a player to see their stats and events for this game.</p>
          <select
            className="form-select form-select-sm bg-dark text-light border-0"
            value={selectedSkaterName}
            onChange={(e) => setSelectedSkaterName(e.target.value)}
            style={{ maxWidth: '280px' }}
          >
            <option value="">Select a skater…</option>
            {skaters.map((p) => (
              <option key={p.name} value={p.name}>
                #{p.number || '—'} · {p.name}
              </option>
            ))}
            {skaters.length > 0 && (
              <option value="__all__">All skaters</option>
            )}
          </select>
        </div>

        {selectedPlayer ? (
          <div className="skater-performance-card rounded p-3 mb-3" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid var(--brand-border)' }}>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <span className="fw-bold" style={{ color: 'var(--brand-gold)', fontSize: '1.1rem' }}>{selectedPlayer.name}</span>
              {selectedPlayer.number != null && selectedPlayer.number !== '' && (
                <span className="badge bg-info">#{selectedPlayer.number}</span>
              )}
              {selectedPlayer.position && (
                <span className="badge bg-secondary">{selectedPlayer.position}</span>
              )}
            </div>
            <div className="d-flex flex-wrap gap-3 mb-3" style={{ fontSize: '0.95rem' }}>
              <span><strong className="text-muted-brand">G</strong> <span className="fw-bold">{selectedPlayer.goals ?? 0}</span></span>
              <span><strong className="text-muted-brand">A</strong> <span className="fw-bold">{selectedPlayer.assists ?? 0}</span></span>
              <span><strong className="text-muted-brand">P</strong> <span className="fw-bold" style={{ color: 'var(--brand-gold)' }}>{selectedPlayer.points ?? 0}</span></span>
              <span>
                <strong className="text-muted-brand">+/-</strong>{' '}
                <span className={`badge ${selectedPlayer.plusMinus > 0 ? 'bg-success' : selectedPlayer.plusMinus < 0 ? 'bg-danger' : 'bg-secondary'}`}>
                  {selectedPlayer.plusMinus > 0 ? '+' : ''}{selectedPlayer.plusMinus ?? 0}
                </span>
              </span>
              <span><strong className="text-muted-brand">Penalties</strong> {selectedPlayer.penalties ?? 0}</span>
              <span><strong className="text-muted-brand">PIM</strong> {selectedPlayer.pim != null ? Number(selectedPlayer.pim).toFixed(2) : '0.00'}</span>
            </div>
            {playerEvents.length > 0 ? (
              <div>
                <div className="small text-muted-brand mb-2" style={{ fontWeight: '600' }}>This game</div>
                <ul className="list-group list-group-flush" style={{ background: 'transparent' }}>
                  {playerEvents.map((ev) => (
                    <li key={ev.index} className="list-group-item d-flex align-items-center gap-2" style={{ background: 'transparent', color: '#e6e8ec', borderColor: 'var(--brand-border)', padding: '0.5rem 0' }}>
                      {ev.period != null && (
                        <span className="badge bg-danger" style={{ fontSize: '0.7rem' }}>{ev.period <= 3 ? `P${ev.period}` : 'OT'}</span>
                      )}
                      <span className="small text-muted-brand">{ev.time}</span>
                      {ev.kind === 'goal' && <span style={{ color: 'var(--brand-gold)', fontWeight: '600' }}>Goal</span>}
                      {ev.kind === 'assist' && <span style={{ color: 'var(--brand-teal)' }}>Assist</span>}
                      {ev.kind === 'penalty' && <span className="text-warning">{ev.label}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="small text-muted-brand">No goals, assists, or penalties recorded this game.</div>
            )}
          </div>
        ) : (
          <div className="table-responsive mt-2">
            <table className="table table-dark-glad table-borderless align-middle" style={{ fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  {['Name','#','Pos','G','A','P','+/-','Penalties','PIM'].map(h=> (
                    <th key={h} className="small">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lineup.filter(p=>!p.isGoalie).map((p,idx)=> (
                  <tr key={`${p.name}-${idx}`}>
                    <td>{p.name}</td>
                    <td>{p.number}</td>
                    <td>{p.position}</td>
                    <td>{p.goals}</td>
                    <td>{p.assists}</td>
                    <td>{p.points}</td>
                    <td>{p.plusMinus ?? 0}</td>
                    <td>{p.penalties}</td>
                    <td>{p.pim.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    )}
  </div>
</div>

            {/* Roster Management - MOVED TO BOTTOM */}
            <div className="card card-glad mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="section-title h5 mb-0">Roster Management</div>
                  <button 
                    className="btn btn-neutral btn-sm" 
                    onClick={() => {
                      toggleSection('roster');
                      setIsRosterVisible(v => !v);
                    }}
                  >
                    {sectionVisibility.roster ? 'Hide' : 'Show'}
                  </button>
                </div>

                {sectionVisibility.roster && (
                  <>
                    {/* Sort Dropdown */}
                    <div className="mb-3">
                      <label className="small text-muted-brand mb-1">Sort Players By</label>
                      <select 
                        className="form-select form-select-sm bg-dark text-light border-0"
                        value={rosterSortBy}
                        onChange={(e) => setRosterSortBy(e.target.value)}
                      >
                        <option value="default">Order Added</option>
                        <option value="firstName">First Name (A-Z)</option>
                        <option value="lastName">Last Name (A-Z)</option>
                        <option value="number">Jersey Number</option>
                        <option value="plusMinus">Plus/Minus (High to Low)</option>
                      </select>
                    </div>

                    <div className="small text-muted-brand mt-2">Upload CSV header: <strong>name, number, position</strong></div>
                    <input type="file" accept=".csv" className="form-control form-control-sm bg-dark text-light border-0 mt-2" onChange={handleRosterUpload} />
                    {rosterMessage && <div className="mt-2 text-success">{rosterMessage}</div>}

                    <div className="small text-muted-brand mt-3 mb-2">
                      <strong>Add Player:</strong> Fill in player details below
                    </div>
                    <div className="row g-2 mt-2">
                      <div className="col-12 col-md-5">
                        <label className="small text-muted-brand mb-1">Player Name *</label>
                        <input 
                          className="form-control form-control-sm bg-dark text-light border-0" 
                          placeholder="Enter first and last name" 
                          value={inputName} 
                          onChange={(e)=>setInputName(e.target.value)} 
                          onKeyDown={(e)=>{ if (e.key==='Enter') handleAddPlayer(); }} 
                        />
                      </div>
                      <div className="col-6 col-md-2">
                        <label className="small text-muted-brand mb-1">Jersey #</label>
                        <select 
                          className="form-select form-select-sm bg-dark text-light border-0" 
                          value={inputNumber} 
                          onChange={(e)=>setInputNumber(e.target.value)}
                        >
                          <option value="">--</option>
                          {Array.from({length: 99}, (_, i) => i + 1).map(num => (
                            <option key={num} value={num}>{num}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-6 col-md-3">
                        <label className="small text-muted-brand mb-1">Position</label>
                        <select 
                          className="form-select form-select-sm bg-dark text-light border-0" 
                          value={inputPosition} 
                          onChange={(e)=>setInputPosition(e.target.value)}
                        >
                          <option value="">Select</option>
                          <option value="F">Forward</option>
                          <option value="D">Defense</option>
                          <option value="G">Goalie</option>
                        </select>
                      </div>
                      <div className="col-12 col-md-2 d-grid align-self-end">
                        <button className="btn btn-sm btn-teal" onClick={handleAddPlayer}>Add Player</button>
                      </div>
                    </div>

                    <ul className="list-group mt-2">
  {sortedLineup.map((p, idx)=> (
    <li key={`${p.name}-${idx}`} className="list-group-item d-flex justify-content-between align-items-center"
        style={{ background:'var(--brand-card)', color:'#e6e8ec', borderColor:'var(--brand-border)', padding: '0.5rem 0.75rem' }}>
      {editingPlayerName === p.name ? (
        <div className="w-100 d-flex flex-wrap gap-2 align-items-center">
          <div className="d-flex gap-2 flex-grow-1 flex-wrap">
            <input className="form-control form-control-sm bg-dark text-light border-0"
                   style={{ minWidth: '120px' }}
                   placeholder="Name"
                   value={newPlayerName}
                   onChange={(e)=>setNewPlayerName(e.target.value)}
                   onKeyDown={(e)=>{ if (e.key==='Enter') handleSavePlayerEdit(); }}/>
            <select className="form-select form-select-sm bg-dark text-light border-0" style={{ width: '4rem' }}
                    value={newPlayerNumber}
                    onChange={(e)=>setNewPlayerNumber(e.target.value)}>
              <option value="">#</option>
              {Array.from({ length: 99 }, (_, i) => i + 1).map(num => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
            <select className="form-select form-select-sm bg-dark text-light border-0" style={{ width: '6rem' }}
                    value={newPlayerPosition}
                    onChange={(e)=>setNewPlayerPosition(e.target.value)}>
              <option value="">Pos</option>
              <option value="F">F</option>
              <option value="D">D</option>
              <option value="G">G</option>
            </select>
          </div>
          <button className="btn btn-teal btn-sm" onClick={handleSavePlayerEdit}>Save</button>
          <button className="btn btn-danger btn-sm" onClick={handleCancelPlayerEdit}>Cancel</button>
        </div>
      ) : (
        <>
          <div className="d-flex align-items-center gap-2 flex-grow-1">
            <span className="fw-semibold">{p.name}</span>
            {p.number && <span className="badge bg-info">#{p.number}</span>}
            {p.position && <span className="badge bg-secondary">{p.position}</span>}
            {p.isGoalie && <span className="chip-goalie">Goalie</span>}
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-neutral btn-sm" onClick={()=>handleEditPlayer(p)}>Edit</button>
            {p.isGoalie ? (
              <button className="btn btn-neutral btn-sm" onClick={()=>handleSetGoalie(p.name)} title="Remove as goalie">Remove G</button>
            ) : (
              <button className="btn btn-neutral btn-sm" onClick={()=>handleSetGoalie(p.name)} title="Set as goalie">Set G</button>
            )}
            <button className="btn btn-danger btn-sm" onClick={()=>handleRemovePlayer(p.name)}>Remove</button>
          </div>
        </>
      )}
    </li>
  ))}
</ul>
                  </>
                )}
              </div>
            </div>
          </>
        )}
        {currentGame && currentGame.archived && (
          <div className="card card-glad mb-4">
            <div className="card-body text-center py-5">
              <div className="mb-3" style={{ fontSize: '3rem' }}>🏁</div>
              <div className="section-title h5 mb-2">This game is archived</div>
              <p className="text-muted-brand mb-3">Archived games can't be edited. Create a new game above to track stats.</p>
              <div className="small text-muted-brand">
                {currentGame?.details?.opponent || 'Game'} · {currentGame?.details?.date || '—'} · Final: {gameData.score?.us ?? 0}–{gameData.score?.them ?? 0}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}